import { isPlatform } from "@ionic/core";

import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import { App } from "@capacitor/app";

import {
  IUser,
  IGuidelineSummary,
  IGuideline,
  IAlgorithm,
  IAnnotation,
  IBookmark,
  IRecentlyViewed,
  IPatientAge,
  ISignificantChange,
  ISignificantChangeAck,
  IAppFeature,
  IFurtherResource,
  IClinicalNotice,
  IClinicalNoticeAck,
  IPGDTrustInfo,
  ITrustPin,
  IPGDAck,
  IPathwayData,
  IDashboardCardState,
  ILastOpenedGuidelineVersions,
  IScreenProps,
  IAppSettings,
  IRecentSearch,
  ISearchResult,
  ILocalUsageLogItem,
  IGLAck,
  IHelpCentreItem,
  ITrustInfo,
  IAbbreviation,
  IPathway,
  IPathwayBypassNumber,
  IPathwayWhatGoesWhere,
  IStaffGroup,
  IRegion,
  ICFRbyTrustItem,
  INotification,
  ISectionColor,
  IAppNotice,
  IRestrictedSection,
} from "../Interfaces";

// JSON

// images
import LogoTrustACF from "../assets/trustLogos/Logo_Trust_ACF@3x.png";
import LogoTrustBRISTOW from "../assets/trustLogos/Logo_Trust_BRISTOW@3x.png";
import LogoTrustClass from "../assets/trustLogos/Logo_Trust_Class@3x.png";
import LogoTrustEEAST from "../assets/trustLogos/Logo_Trust_EEAST@3x.png";
import LogoTrustEMAS from "../assets/trustLogos/Logo_Trust_EMAS@3x.png";
import LogoTrustIOM from "../assets/trustLogos/Logo_Trust_IOM@3x.png";
import LogoTrustJAS from "../assets/trustLogos/Logo_Trust_JAS@3x.png";
import LogoTrustLAS from "../assets/trustLogos/Logo_Trust_LAS@3x.png";
import LogoTrustNEAS from "../assets/trustLogos/Logo_Trust_NEAS@3x.png";
import LogoTrustNEOM from "../assets/trustLogos/Logo_Trust_NEOM@3x.png";
import LogoTrustNIAS from "../assets/trustLogos/Logo_Trust_NIAS@3x.png";
import LogoTrustNWAS from "../assets/trustLogos/Logo_Trust_NWAS@3x.png";
import LogoTrustROLE1 from "../assets/trustLogos/Logo_Trust_ROLE1@3x.png";
import LogoTrustSAS from "../assets/trustLogos/Logo_Trust_SAS@3x.png";
import LogoTrustSCAS from "../assets/trustLogos/Logo_Trust_SCAS@3x.png";
import LogoTrustSECAMB from "../assets/trustLogos/Logo_Trust_SECAMB@3x.png";
import LogoTrustSJA from "../assets/trustLogos/Logo_Trust_SJA@3x.png";
import LogoTrustSJARS from "../assets/trustLogos/Logo_Trust_SJARS@3x.png";
import LogoTrustSWAST from "../assets/trustLogos/Logo_Trust_SWAST@3x.png";
import LogoTrustTST from "../assets/trustLogos/Logo_Trust_TST@3x.png";
import LogoTrustWAS from "../assets/trustLogos/Logo_Trust_WAS@3x.png";
import LogoTrustWMAS from "../assets/trustLogos/Logo_Trust_WMAS@3x.png";
import LogoTrustYAS from "../assets/trustLogos/Logo_Trust_YAS@3x.png";

import _ from "lodash";

let useInnodataGuidelines = false;

export class DataController {
  async updateAppSettings(app: IScreenProps["app"]) {
    let appSettings: IAppSettings | null = null;

    try {
      let contents = await Filesystem.readFile({
        path: `appSettings.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      appSettings = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    if (appSettings == null) {
      appSettings = (await import("../assets/json/appSettings.json")).default;
    }

    let serverUrl = appSettings[app].serverUrl;
    let showOlark = appSettings[app].showOlark;
    let ssoTrusts = appSettings[app].ssoTrusts;

    try {
      await Preferences.set({ key: "serverUrl", value: serverUrl });
      await Preferences.set({ key: "showOlark", value: showOlark ? showOlark.toString() : "false" });
      await Preferences.set({ key: "ssoTrusts", value: ssoTrusts ? JSON.stringify(ssoTrusts) : JSON.stringify([]) });
    } catch (error) {
      console.log(error);
    }
  }

  async getGuidelineSummaries(trust: string, app: IScreenProps["app"]): Promise<IGuidelineSummary[]> {
    // returns combined bundled GLList.json and downloaded GLUpdates.json
    try {
      // get local GLUpdates
      let guidelineSummaryUpdates = [];

      try {
        let contents = await Filesystem.readFile({
          path: `GLUpdates.json`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        guidelineSummaryUpdates = JSON.parse(contents.data as string);
      } catch (error) {
        console.log(error);
      }

      let bundledGuidelineSummaries: IGuidelineSummary[] = (await import("../../Data/Feed-Sup/GLList.json")).default;
      if (useInnodataGuidelines) {
        bundledGuidelineSummaries = [];
      }

      if (trust !== "") {
        try {
          let bundledTrustGuidelineSummaries: IGuidelineSummary[] = (
            await import(`../../Data/${trust}/JSON/CMGuidelineList.json`)
          ).default;
          if (useInnodataGuidelines === false) {
            bundledGuidelineSummaries = bundledGuidelineSummaries.concat(bundledTrustGuidelineSummaries);
          }

          for (let trustGuidelineSummary of bundledTrustGuidelineSummaries) {
            trustGuidelineSummary.trust = trust;
          }
        } catch (error) {
          console.log("Couldn't find bundled trust CMGuidelineList.json");
        }

        // get local trust CMGLUpdates
        try {
          let contents = await Filesystem.readFile({
            path: `${trust}-CMGLUpdates.json`,
            directory: Directory.Data,
            encoding: Encoding.UTF8,
          });
          let trustGuidelineSummaryUpdates = JSON.parse(contents.data as string);

          for (let guidelineSummaryUpdate of trustGuidelineSummaryUpdates) {
            guidelineSummaryUpdate.trust = trust;
          }

          guidelineSummaryUpdates = guidelineSummaryUpdates.concat(trustGuidelineSummaryUpdates);
        } catch (error) {
          console.log(error);
        }
      }

      // combine bundled guideline and trust guidelines with guideline and trust guideline updates to get latest version of all guideline summaries
      let allGuidelineSummaries = [];

      for (
        let guidelineSummaryIndex = 0;
        guidelineSummaryIndex < bundledGuidelineSummaries.length;
        guidelineSummaryIndex++
      ) {
        let guidelineSummary: IGuidelineSummary = bundledGuidelineSummaries[guidelineSummaryIndex];
        let guidelineSummaryUpdateFound = false;

        for (
          let guidelineSummaryUpdateIndex = 0;
          guidelineSummaryUpdateIndex < guidelineSummaryUpdates.length;
          guidelineSummaryUpdateIndex++
        ) {
          let guidelineSummaryUpdate: IGuidelineSummary = guidelineSummaryUpdates[guidelineSummaryUpdateIndex];

          if (guidelineSummary.GLID === guidelineSummaryUpdate.GLID) {
            let guidelineSummaryUpdateIsSameOrLater = false;
            let guidelineSummaryHasBeenRemoved = false;

            if (guidelineSummary.GLID === "S0020") {
              let test = "";
            }

            const guidelineSummaryMajor = parseInt(guidelineSummary.publ_major_version_no);
            const guidelineSummaryMinor = parseInt(guidelineSummary.publ_minor_version_no);
            const updateSummaryMajor = parseInt(guidelineSummaryUpdate.publ_major_version_no);
            const updateSummaryMinor = parseInt(guidelineSummaryUpdate.publ_minor_version_no);

            if (isNaN(guidelineSummaryMajor) || isNaN(guidelineSummaryMinor)) {
              guidelineSummaryUpdateIsSameOrLater = true;
            }

            if (updateSummaryMajor > guidelineSummaryMajor) {
              guidelineSummaryUpdateIsSameOrLater = true;
            }

            if (updateSummaryMajor === guidelineSummaryMajor && updateSummaryMinor >= guidelineSummaryMinor) {
              guidelineSummaryUpdateIsSameOrLater = true;
            }

            if (guidelineSummaryUpdate.remove != null && guidelineSummaryUpdate.remove === 1) {
              guidelineSummaryHasBeenRemoved = true;
            }

            if (!guidelineSummaryHasBeenRemoved) {
              if (guidelineSummaryUpdateIsSameOrLater) {
                allGuidelineSummaries.push(guidelineSummaryUpdate);
              } else {
                allGuidelineSummaries.push(guidelineSummary);
              }
            }

            guidelineSummaryUpdateFound = true;
            break;
          }
        }

        // add guideline summary if there isn't an update for it
        if (!guidelineSummaryUpdateFound) {
          allGuidelineSummaries.push(guidelineSummary);
        }
      }

      // add any guideline summary updates that aren't in the bundle
      for (
        let guidelineSummaryUpdateIndex = 0;
        guidelineSummaryUpdateIndex < guidelineSummaryUpdates.length;
        guidelineSummaryUpdateIndex++
      ) {
        let guidelineSummaryUpdate: IGuidelineSummary = guidelineSummaryUpdates[guidelineSummaryUpdateIndex];
        if (
          allGuidelineSummaries.some((guidelineSummary) => guidelineSummary.GLID === guidelineSummaryUpdate.GLID) ===
          false
        ) {
          if (guidelineSummaryUpdate.remove !== 1) {
            allGuidelineSummaries.push(guidelineSummaryUpdate);
          }
        }
      }

      // add CFR only guidelines
      if (app === "CFR") {
        try {
          let contents = await Filesystem.readFile({
            path: `${trust}-CFRIndex.json`,
            directory: Directory.Data,
            encoding: Encoding.UTF8,
          });
          let cfrGuidelineSummaries = JSON.parse(contents.data as string);

          for (
            let cfrGuidelineSummaryIndex = 0;
            cfrGuidelineSummaryIndex < cfrGuidelineSummaries.length;
            cfrGuidelineSummaryIndex++
          ) {
            let cfrGuidelineSummary: IGuidelineSummary = cfrGuidelineSummaries[cfrGuidelineSummaryIndex];

            cfrGuidelineSummary.trust = trust;

            let cfrGuidelineSummaryFound = false;

            for (
              let guidelineSummaryIndex = 0;
              guidelineSummaryIndex < allGuidelineSummaries.length;
              guidelineSummaryIndex++
            ) {
              let guidelineSummary: IGuidelineSummary = allGuidelineSummaries[guidelineSummaryIndex];

              if (cfrGuidelineSummary.GLID === guidelineSummary.GLID) {
                cfrGuidelineSummaryFound = true;

                let cfrGuidelineSummaryIsSameOrLater = false;

                const guidelineSummaryMajor = parseInt(guidelineSummary.publ_major_version_no);
                const guidelineSummaryMinor = parseInt(guidelineSummary.publ_minor_version_no);
                const cfrGuidelineSummaryMajor = parseInt(cfrGuidelineSummary.publ_major_version_no);
                const cfrGuidelineSummaryMinor = parseInt(cfrGuidelineSummary.publ_minor_version_no);

                if (cfrGuidelineSummaryMajor > guidelineSummaryMajor) {
                  cfrGuidelineSummaryIsSameOrLater = true;
                }

                if (
                  cfrGuidelineSummaryMajor === guidelineSummaryMajor &&
                  cfrGuidelineSummaryMinor >= guidelineSummaryMinor
                ) {
                  cfrGuidelineSummaryIsSameOrLater = true;
                }

                if (cfrGuidelineSummaryIsSameOrLater) {
                  allGuidelineSummaries[guidelineSummaryIndex] = cfrGuidelineSummary;
                }

                break;
              }
            }

            if (cfrGuidelineSummaryFound === false) {
              allGuidelineSummaries.push(cfrGuidelineSummary);
            }
          }
        } catch (error) {
          console.log(error);
        }
      }

      // remove any guideline summaries that don't have valid format - loop through backwards to make easier to remove
      for (
        let guidelineSummaryIndex = allGuidelineSummaries.length - 1;
        guidelineSummaryIndex >= 0;
        guidelineSummaryIndex--
      ) {
        let guidelineSummary = allGuidelineSummaries[guidelineSummaryIndex];

        if (
          guidelineSummary.GLID == null ||
          guidelineSummary.publ_major_version_no == null ||
          guidelineSummary.publ_minor_version_no == null
        ) {
          // missing required fields so shouldn't be included
          allGuidelineSummaries.splice(guidelineSummaryIndex, 1);
        }
      }

      // for (let guidelineSummary of allGuidelineSummaries) {
      //   let sections = guidelineSummary.Section.trim().split(" ");
      //   if (guidelineSummary.GLID === "SWT0006") {
      //     let test = "";
      //   }
      //   if (guidelineSummary.Section.includes(",") === false && guidelineSummary.Section !== "Clinical Guidelines") {
      //     guidelineSummary.Section = sections.join(", ");
      //   }
      // }

      return allGuidelineSummaries;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async getGuidelines(trust: string, app: IScreenProps["app"]): Promise<IGuideline[]> {
    let allGuidelines: IGuideline[] = [];

    try {
      let guidelinesSummaryList = await this.getGuidelineSummaries(trust, app);

      let downloadedGuidelines = await this.readGuidelinesDirectory();

      for (
        let guidelineSummaryIndex = 0;
        guidelineSummaryIndex < guidelinesSummaryList.length;
        guidelineSummaryIndex++
      ) {
        if (guidelineSummaryIndex % 10 === 0) {
          await this.waitForMilliseconds(10);
        }

        const guidelineSummary = guidelinesSummaryList[guidelineSummaryIndex];

        let guidelineToAdd = null;
        let downloadedGuideline = null;
        let bundledGuideline = null;

        // get downloaded guideline if exists
        if (downloadedGuidelines.includes(`${guidelineSummary.GLID}.json`)) {
          try {
            downloadedGuideline = await this.getDownloadedGuideline(guidelineSummary);
          } catch (error) {
            console.log(error);
          }
        }

        // get bundled guideline if exists
        try {
          bundledGuideline = await this.getBundledGuideline(guidelineSummary);
        } catch (error) {
          console.log(error);
        }

        // add the latest version of guideline
        if (downloadedGuideline && bundledGuideline) {
          // both exist so compare to see which is later
          let downloadedGuidelineIsSameOrLater = false;

          const bundledGuidelineMajor = parseInt(bundledGuideline.publ_major_version_no);
          const bundledGuidelineMinor = parseInt(bundledGuideline.publ_minor_version_no);
          const downloadedGuidelineMajor = parseInt(downloadedGuideline.publ_major_version_no);
          const downloadedGuidelineMinor = parseInt(downloadedGuideline.publ_minor_version_no);

          if (downloadedGuidelineMajor > bundledGuidelineMajor) {
            downloadedGuidelineIsSameOrLater = true;
          }

          if (downloadedGuidelineMajor === bundledGuidelineMajor && downloadedGuidelineMinor >= bundledGuidelineMinor) {
            downloadedGuidelineIsSameOrLater = true;
          }

          if (downloadedGuidelineIsSameOrLater) {
            guidelineToAdd = downloadedGuideline;
          } else {
            guidelineToAdd = bundledGuideline;
          }
        } else if (downloadedGuideline) {
          guidelineToAdd = downloadedGuideline;
        } else if (bundledGuideline) {
          guidelineToAdd = bundledGuideline;
        }

        if (guidelineToAdd != null) {
          allGuidelines.push(guidelineToAdd);
        }
      }

      // remove any guideline summaries that don't have valid format - loop through backwards to make easier to remove
      for (let guidelineIndex = allGuidelines.length - 1; guidelineIndex >= 0; guidelineIndex--) {
        let guideline = allGuidelines[guidelineIndex];

        if (
          guideline.GLID == null ||
          guideline.publ_major_version_no == null ||
          guideline.publ_minor_version_no == null
        ) {
          // missing required fields so shouldn't be included
          allGuidelines.splice(guidelineIndex, 1);
        }
      }
    } catch (error) {
      console.log(error);
    }

    return allGuidelines;
  }

  async getGuideline(guidelineSummary: IGuidelineSummary): Promise<IGuideline | null> {
    let guideline = null;
    let downloadedGuideline = null;
    let bundledGuideline = null;

    // get downloaded guideline if exists
    try {
      downloadedGuideline = await this.getDownloadedGuideline(guidelineSummary);
    } catch (error) {
      console.log(error);
    }

    // get bundled guideline if exists
    try {
      bundledGuideline = await this.getBundledGuideline(guidelineSummary);
    } catch (error) {
      console.log(error);
    }

    // use the latest version of guideline
    if (downloadedGuideline && bundledGuideline) {
      // both exist so compare to see which is later
      let downloadedGuidelineIsSameOrLater = false;

      const guidelineSummaryMajor = parseInt(guidelineSummary.publ_major_version_no);
      const guidelineSummaryMinor = parseInt(guidelineSummary.publ_minor_version_no);
      const bundledGuidelineMajor = parseInt(bundledGuideline.publ_major_version_no);
      const bundledGuidelineMinor = parseInt(bundledGuideline.publ_minor_version_no);
      const downloadedGuidelineMajor = parseInt(downloadedGuideline.publ_major_version_no);
      const downloadedGuidelineMinor = parseInt(downloadedGuideline.publ_minor_version_no);

      if (downloadedGuidelineMajor === guidelineSummaryMajor && downloadedGuidelineMinor === guidelineSummaryMinor) {
        guideline = downloadedGuideline;
      } else if (bundledGuidelineMajor === guidelineSummaryMajor && bundledGuidelineMinor === guidelineSummaryMinor) {
        guideline = bundledGuideline;
      } else {
        if (downloadedGuidelineMajor > bundledGuidelineMajor) {
          downloadedGuidelineIsSameOrLater = true;
        }

        if (downloadedGuidelineMajor === bundledGuidelineMajor && downloadedGuidelineMinor >= bundledGuidelineMinor) {
          downloadedGuidelineIsSameOrLater = true;
        }

        if (downloadedGuidelineIsSameOrLater) {
          guideline = downloadedGuideline;
        } else {
          guideline = bundledGuideline;
        }
      }
    } else if (downloadedGuideline) {
      guideline = downloadedGuideline;
    } else if (bundledGuideline) {
      guideline = bundledGuideline;
    }

    return guideline;
  }

  async getBundledGuideline(guidelineSummary: IGuidelineSummary): Promise<IGuideline | null> {
    let guidelineJson: IGuideline | null = null;

    if (guidelineSummary.trust != null) {
      try {
        let bundledJson = (await import(`../../Data/${guidelineSummary.trust}/JSON/${guidelineSummary.GLID}.json`))
          .default;
        if (bundledJson != null) {
          guidelineJson = bundledJson.CMG;
          if (guidelineJson == null) {
            guidelineJson = bundledJson.guideline;
          }
          if (guidelineJson != null) {
            guidelineJson.trust = guidelineSummary.trust;
            if (guidelineJson.title == null && guidelineJson.publ_section_title != null) {
              guidelineJson.title = guidelineJson.publ_section_title;
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      let directory = "Guidelines";
      if (this.isDrug(guidelineSummary) || this.isPageForAge(guidelineSummary.GLID)) {
        // if drug isn't a special situation drug then look in drugs directory
        var SODrug = ["S0030", "S0040", "S0050", "S0060", "S0070", "S0080", "S0090"];
        if (SODrug.indexOf(guidelineSummary.GLID) === -1) {
          directory = "Drugs";
        }
      }
      try {
        let bundledJson = (await import(`../../Data/Feed-Sup/${directory}/${guidelineSummary.GLID}.json`)).default;
        if (bundledJson != null) {
          guidelineJson = bundledJson.guideline;
        }
      } catch (error) {
        console.log(error);
      }
    }

    return guidelineJson;
  }

  async getBundledGuidelineVersion(
    guidelineSummary: IGuidelineSummary,
    trust: string
  ): Promise<{ publ_major_version_no: string; publ_minor_version_no: string } | null> {
    // get bundled guidline summaries (and trust ones)
    let bundledGuidelineSummaries: IGuidelineSummary[] = (await import("../../Data/Feed-Sup/GLList.json")).default;
    if (trust !== "") {
      try {
        let bundledTrustGuidelineSummaries: IGuidelineSummary[] = (
          await import(`../../Data/${trust}/JSON/CMGuidelineList.json`)
        ).default;
        bundledGuidelineSummaries = bundledGuidelineSummaries.concat(bundledTrustGuidelineSummaries);

        for (let trustGuidelineSummary of bundledTrustGuidelineSummaries) {
          trustGuidelineSummary.trust = trust;
        }
      } catch (error) {
        console.log("Couldn't find bundled trust CMGuidelineList.json");
      }
    }

    let matchingGuidelineSummary = bundledGuidelineSummaries.find(
      (bundledGuidelineSummary) => bundledGuidelineSummary.GLID === guidelineSummary.GLID
    );

    if (matchingGuidelineSummary != null) {
      return {
        publ_major_version_no: matchingGuidelineSummary.publ_major_version_no,
        publ_minor_version_no: matchingGuidelineSummary.publ_minor_version_no,
      };
    }

    return null;
  }

  async getDownloadedGuideline(guidelineSummary: IGuidelineSummary): Promise<IGuideline | null> {
    let guidelineJson: IGuideline | null = null;

    try {
      let contents = await Filesystem.readFile({
        path: `guidelines/${guidelineSummary.GLID}.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });

      if (contents != null) {
        guidelineJson = JSON.parse(contents.data as string);
        if (guidelineJson != null && guidelineSummary.trust != null) {
          guidelineJson.trust = guidelineSummary.trust;

          if (guidelineJson.title == null && guidelineJson.publ_section_title != null) {
            guidelineJson.title = guidelineJson.publ_section_title;
          }
        }
      }
    } catch (error) {
      // console.log(error);
    }

    return guidelineJson;
  }

  async getImages(
    guidelineSummaries: IGuidelineSummary[],
    algorithms: IAlgorithm[],
    pathwayData: IPathwayData | null,
    annotations: IAnnotation[],
    trust: string
  ) {
    let finalImages = [];
    let images = [];

    // add guideline images
    for (let guidelineSummaryIndex = 0; guidelineSummaryIndex < guidelineSummaries.length; guidelineSummaryIndex++) {
      let guidelineSummary = guidelineSummaries[guidelineSummaryIndex];

      if (guidelineSummary.images) {
        for (let imageIndex = 0; imageIndex < guidelineSummary.images.length; imageIndex++) {
          let imageFile = guidelineSummary.images[imageIndex];

          let existingImage = images.find((image) => image.imageName === imageFile.imageName);
          if (!existingImage) {
            images.push({ imageName: imageFile.imageName, trust: guidelineSummary.trust });
          }
        }
      }
    }

    // add algorithm images
    for (let algorithmIndex = 0; algorithmIndex < algorithms.length; algorithmIndex++) {
      let algorithm = algorithms[algorithmIndex];

      if (algorithm.image) {
        let existingImage = images.find((image) => image.imageName === algorithm.image);
        if (!existingImage) {
          images.push({ imageName: algorithm.image });
        }
      }
    }

    // add pathway images
    if (pathwayData != null) {
      for (let pathwayIndex = 0; pathwayIndex < pathwayData.Pathways.length; pathwayIndex++) {
        let pathway = pathwayData.Pathways[pathwayIndex];
        let pathwayImages = pathway.fields.PathwayImage;
        if (pathwayImages != null) {
          for (let pathwayImageIndex = 0; pathwayImageIndex < pathwayImages.length; pathwayImageIndex++) {
            let pathwayImage = pathwayImages[pathwayImageIndex];
            let pathwayImageName = this.getPathWayImageName(pathway, pathwayImage.filename);
            images.push({ imageName: pathwayImageName, trust: trust });
          }
        }
      }
    }

    // add pathway whatGoesWhere images
    if (pathwayData != null) {
      for (let pathwayIndex = 0; pathwayIndex < pathwayData.Whatgoeswhere.length; pathwayIndex++) {
        let pathway = pathwayData.Whatgoeswhere[pathwayIndex];
        let pathwayImages = pathway.fields.PathwayImage;
        if (pathwayImages != null) {
          for (let pathwayImageIndex = 0; pathwayImageIndex < pathwayImages.length; pathwayImageIndex++) {
            let pathwayImage = pathwayImages[pathwayImageIndex];
            let pathwayImageName = this.getPathwayWhatGoesWhereImageName(pathway, pathwayImage.filename);
            images.push({ imageName: pathwayImageName, trust: trust });
          }
        }
      }
    }

    if (annotations != null) {
      for (let annotationIndex = 0; annotationIndex < annotations.length; annotationIndex++) {
        let annotation = annotations[annotationIndex];
        let annotationImages = annotation.Images;
        if (annotationImages != null) {
          for (let annotationImageIndex = 0; annotationImageIndex < annotationImages.length; annotationImageIndex++) {
            let annotationImage = annotationImages[annotationImageIndex];
            let annotationImageName = this.getAnnotationImageName(annotation, annotationImage.filename);
            images.push({ imageName: annotationImageName, trust: trust });
          }
        }
      }
    }

    // get each image from bundle or download directory and get the base64 data for it
    for (let imageNameIndex = 0; imageNameIndex < images.length; imageNameIndex++) {
      let anImage = images[imageNameIndex];
      let contents: { data: any };

      let imageData = null;
      let trust = "";

      if (anImage.imageName === "MajorTraumaEMASTraumaMap2.jpg") {
        let test = "";
      }

      // if (anImage.guidelineSummary != null && anImage.guidelineSummary.trust != null) {
      //   trust = anImage.guidelineSummary.trust;
      // }

      // try bundled image, then downloaded image
      try {
        let imageData = await this.getBundledImageData(anImage);
        contents = {
          data: imageData,
        };
      } catch (error) {
        // try downloaded image
        try {
          contents = await Filesystem.readFile({
            path: `images/${anImage.imageName}`,
            directory: Directory.Data,
          });
          let imageType = "jpeg";
          if (anImage.imageName.includes(".png") || anImage.imageName.includes(".PNG")) {
            imageType = "png";
          }
          contents.data = "data:image/" + imageType + ";base64, " + contents.data;
        } catch (error) {
          console.log(error);
          contents = {
            data: "",
          };
        }
      }

      if (contents != null && contents.data != null) {
        contents.data = contents.data.replace(/"/g, "");

        let image = {
          name: anImage.imageName,
          data: contents.data,
        };

        finalImages.push(image);
      }
    }

    return finalImages;
  }

  getBundledImageData(image: { imageName: string; trust?: string }) {
    try {
      let url;

      // console.log('loading bundled image: ' + image.imageName);

      if (image.trust != null && image.trust !== "") {
        switch (image.trust) {
          case "ACF":
            url = require(`../../Data/ACF/img/${image.imageName}`);
            break;
          case "BRISTOW":
            url = require(`../../Data/BRISTOW/img/${image.imageName}`);
            break;
          case "Class":
            url = require(`../../Data/Class/img/${image.imageName}`);
            break;
          case "EEAST":
            url = require(`../../Data/EEAST/img/${image.imageName}`);
            break;
          case "EMAS":
            url = require(`../../Data/EMAS/img/${image.imageName}`);
            break;
          case "IOM":
            url = require(`../../Data/IOM/img/${image.imageName}`);
            break;
          case "JAS":
            url = require(`../../Data/JAS/img/${image.imageName}`);
            break;
          case "LAS":
            url = require(`../../Data/LAS/img/${image.imageName}`);
            break;
          case "NEAS":
            url = require(`../../Data/NEAS/img/${image.imageName}`);
            break;
          case "NEOM":
            url = require(`../../Data/NEOM/img/${image.imageName}`);
            break;
          case "NIAS":
            url = require(`../../Data/NIAS/img/${image.imageName}`);
            break;
          case "NWAS":
            url = require(`../../Data/NWAS/img/${image.imageName}`);
            break;
          case "ROLE1":
            url = require(`../../Data/ROLE1/img/${image.imageName}`);
            break;
          case "SAS":
            url = require(`../../Data/SAS/img/${image.imageName}`);
            break;
          case "SCAS":
            url = require(`../../Data/SCAS/img/${image.imageName}`);
            break;
          case "SECAMB":
            url = require(`../../Data/SECAMB/img/${image.imageName}`);
            break;
          case "SJA":
            url = require(`../../Data/SJA/img/${image.imageName}`);
            break;
          case "SJARS":
            url = require(`../../Data/SJARS/img/${image.imageName}`);
            break;
          case "SWAST":
            url = require(`../../Data/SWAST/img/${image.imageName}`);
            break;
          case "TST":
            url = require(`../../Data/TST/img/${image.imageName}`);
            break;
          case "WAS":
            url = require(`../../Data/WAS/img/${image.imageName}`);
            break;
          case "WMAS":
            url = require(`../../Data/WMAS/img/${image.imageName}`);
            break;
          case "YAS":
            url = require(`../../Data/YAS/img/${image.imageName}`);
            break;

          default:
            break;
        }
      } else {
        url = require(`../../Data/Feed-Sup/GLimage/${image.imageName}`);
      }

      return url.default;
    } catch (error) {
      // console.log('error getting bundled image' + error);
      throw error;
    }
  }

  async checkImageExists(imageName: string, trust?: string): Promise<boolean> {
    let bundledImageExists = await this.checkBundledImageExists(imageName, trust);
    if (bundledImageExists) {
      return true;
    }

    let downloadedImageExists = await this.checkDownloadedImageExists(imageName);
    if (downloadedImageExists) {
      return true;
    }

    return false;
  }

  async checkDownloadedImageExists(imageName: string): Promise<boolean> {
    let imageExists = false;

    try {
      let contents = await Filesystem.stat({
        path: `images/${imageName}`,
        directory: Directory.Data,
      });

      if (contents != null) {
        imageExists = true;
      }
    } catch (error) {
      console.log(error);
    }

    return imageExists;
  }

  async checkBundledImageExists(imageName: string, trust?: string) {
    try {
      if (trust != null && trust !== "") {
        let url;

        switch (trust) {
          case "ACF":
            url = require(`../../Data/ACF/img/${imageName}`);
            break;
          case "BRISTOW":
            url = require(`../../Data/BRISTOW/img/${imageName}`);
            break;
          case "Class":
            url = require(`../../Data/Class/img/${imageName}`);
            break;
          case "EEAST":
            url = require(`../../Data/EEAST/img/${imageName}`);
            break;
          case "EMAS":
            url = require(`../../Data/EMAS/img/${imageName}`);
            break;
          case "IOM":
            url = require(`../../Data/IOM/img/${imageName}`);
            break;
          case "JAS":
            url = require(`../../Data/JAS/img/${imageName}`);
            break;
          case "LAS":
            url = require(`../../Data/LAS/img/${imageName}`);
            break;
          case "NEAS":
            url = require(`../../Data/NEAS/img/${imageName}`);
            break;
          case "NEOM":
            url = require(`../../Data/NEOM/img/${imageName}`);
            break;
          case "NIAS":
            url = require(`../../Data/NIAS/img/${imageName}`);
            break;
          case "NWAS":
            url = require(`../../Data/NWAS/img/${imageName}`);
            break;
          case "ROLE1":
            url = require(`../../Data/ROLE1/img/${imageName}`);
            break;
          case "SAS":
            url = require(`../../Data/SAS/img/${imageName}`);
            break;
          case "SCAS":
            url = require(`../../Data/SCAS/img/${imageName}`);
            break;
          case "SECAMB":
            url = require(`../../Data/SECAMB/img/${imageName}`);
            break;
          case "SJA":
            url = require(`../../Data/SJA/img/${imageName}`);
            break;
          case "SJARS":
            url = require(`../../Data/SJARS/img/${imageName}`);
            break;
          case "SWAST":
            url = require(`../../Data/SWAST/img/${imageName}`);
            break;
          case "TST":
            url = require(`../../Data/TST/img/${imageName}`);
            break;
          case "WAS":
            url = require(`../../Data/WAS/img/${imageName}`);
            break;
          case "WMAS":
            url = require(`../../Data/WMAS/img/${imageName}`);
            break;
          case "YAS":
            url = require(`../../Data/YAS/img/${imageName}`);
            break;

          default:
            break;
        }

        // url = require(`../../Data/${trust}/img/${imageName}`);

        return true;
      } else {
        const url = require(`../../Data/Feed-Sup/GLimage/${imageName}`);
        return true;
      }
    } catch (error) {
      // console.log(error);
      return false;
    }

    return false;
  }

  async makeDirectories() {
    try {
      await Filesystem.mkdir({
        path: "guidelines",
        directory: Directory.Data,
        recursive: false,
      });
    } catch (error) {
      // console.log(error);
    }

    try {
      await Filesystem.mkdir({
        path: "images",
        directory: Directory.Data,
        recursive: false,
      });
    } catch (error) {
      // console.log(error);
    }

    try {
      await Filesystem.mkdir({
        path: "furtherResourceImages",
        directory: Directory.Data,
        recursive: false,
      });
    } catch (error) {
      // console.log(error);
    }

    try {
      await Filesystem.mkdir({
        path: "helpImages",
        directory: Directory.Data,
        recursive: false,
      });
    } catch (error) {
      // console.log(error);
    }
  }

  async readGuidelinesDirectory(): Promise<string[]> {
    try {
      let directory = await Filesystem.readdir({
        path: "guidelines",
        directory: Directory.Data,
      });

      return directory.files.map((file) => file.name);
    } catch (error) {
      console.log(error);

      try {
        await Filesystem.mkdir({
          path: `guidelines`,
          directory: Directory.Data,
          recursive: false,
        });
      } catch (error) {
        console.log(error);
      }
    }

    return [];
  }

  async readImagesDirectory() {
    try {
      let directory = await Filesystem.readdir({
        path: "images",
        directory: Directory.Data,
      });

      return directory.files;
    } catch (error) {
      console.log(error);

      try {
        await Filesystem.mkdir({
          path: `images`,
          directory: Directory.Data,
          recursive: false,
        });
      } catch (error) {
        console.log(error);
      }
    }

    return [];
  }

  async getAlgorithms(): Promise<IAlgorithm[]> {
    let algorithms: IAlgorithm[] = [];

    let firstDownloadedAlgorithm = null;
    let firstBundledAlgorithm = null;
    let downloadedAlgorithms = [];

    try {
      let contents = await Filesystem.readFile({
        path: `AlgorithmList.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      downloadedAlgorithms = JSON.parse(contents.data as string);
      if (downloadedAlgorithms != null && downloadedAlgorithms.length > 0) {
        firstDownloadedAlgorithm = downloadedAlgorithms[0];
      }
    } catch (error) {
      console.log(error);
    }

    var bundledAlgorithms: IAlgorithm[] = (await import(`../../Data/Feed-Sup/AlgorithmList.json`)).default;
    firstBundledAlgorithm = bundledAlgorithms[0];

    if (firstDownloadedAlgorithm && firstBundledAlgorithm) {
      if (firstBundledAlgorithm.updated != undefined && firstDownloadedAlgorithm.updated != undefined) {
        var downloadAlgorithmsUpdatedDate = Date.parse(firstDownloadedAlgorithm.updated);
        var bundledAlgorithmsUpdatedDate = Date.parse(firstBundledAlgorithm.updated);
        if (downloadAlgorithmsUpdatedDate >= bundledAlgorithmsUpdatedDate) {
          return downloadedAlgorithms;
        } else {
          return bundledAlgorithms;
        }
      }
    } else if (firstDownloadedAlgorithm) {
      return downloadedAlgorithms;
    }

    return bundledAlgorithms;
  }

  async getAnnotations(trust: string, testMode: boolean): Promise<IAnnotation[]> {
    let latestAnnotations: IAnnotation[] = [];

    if (trust === "") {
      return [];
    }

    // returns either bundled notesList.json or downloaded notesList.{trust}.json depending on which is latest
    try {
      // get downloaded annotations
      let downloadedAnnotations: IAnnotation[] = [];

      let path = `notesLists.${trust}.json`;
      if (testMode) {
        path = `notesLists.${trust}.TEST.json`;
      }

      try {
        let contents = await Filesystem.readFile({
          path: path,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        downloadedAnnotations = JSON.parse(contents.data as string);
      } catch (error) {
        console.log(error);
      }

      // get bundled annotations
      let bundledAnnotations: IAnnotation[] = [];

      if (testMode !== true) {
        try {
          bundledAnnotations = (await import(`../../Data/${trust}/JSON/notesLists.json`)).default;
        } catch (error) {
          console.log("Couldn't find bundled trust CMGuidelineList.json");
        }
      }

      // check the updated date in first annotation to see if bundled or downloaded notes are newer
      if (downloadedAnnotations.length > 0 && bundledAnnotations.length > 0) {
        let firstBundledAnnotation = bundledAnnotations[0];
        let firstDownloadedAnnotation = downloadedAnnotations[0];

        if (firstBundledAnnotation.updated && firstDownloadedAnnotation.updated) {
          var bundledAnnotationsUpdatedDate = new Date(firstBundledAnnotation.updated);
          var downloadedAnnotationsUpdatedDate = new Date(firstDownloadedAnnotation.updated);

          if (downloadedAnnotationsUpdatedDate >= bundledAnnotationsUpdatedDate) {
            latestAnnotations = downloadedAnnotations;
          } else {
            latestAnnotations = bundledAnnotations;
          }
        } else {
          latestAnnotations = bundledAnnotations;
        }
      } else if (downloadedAnnotations.length > 0) {
        latestAnnotations = downloadedAnnotations;
      } else if (bundledAnnotations.length > 0) {
        latestAnnotations = bundledAnnotations;
      }

      return latestAnnotations;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  getAnnotationImageName(annotation: IAnnotation, annotationImageFilename: string) {
    let annotationImageName = annotationImageFilename;
    annotationImageName = annotationImageName.replace(/ /g, ""); // todo changed regex - check still works
    return annotationImageName;
  }

  async getPathwayData(trust: string, testMode: boolean): Promise<IPathwayData | null> {
    let latestPathwayData: IPathwayData | null = null;

    if (trust === "") {
      return null;
    }

    // returns either bundled or downloaded json depending on which is latest
    try {
      // get downloaded pathway data
      let downloadedPathwayData: IPathwayData | null = null;

      let path = `${trust}.pathway.json`;
      if (testMode) {
        path = `${trust}.pathway.TEST.json`;
      }

      try {
        let contents = await Filesystem.readFile({
          path: path,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        downloadedPathwayData = JSON.parse(contents.data as string);
      } catch (error) {
        console.log(error);
      }

      // get bundled pathway data
      let bundledPathwayData: IPathwayData | null = null;

      if (testMode !== true) {
        try {
          bundledPathwayData = (await import(`../../Data/${trust}/JSON/${trust}.pathway.json`)).default;
        } catch (error) {
          console.log("Couldn't find bundled trust pathway json");
        }
      }

      // check the updated date to see if bundled or downloaded pathwat data is newer
      if (downloadedPathwayData != null && bundledPathwayData != null) {
        if (bundledPathwayData.updated && downloadedPathwayData.updated) {
          var bundledPathwayDataUpdatedDate = new Date(bundledPathwayData.updated);
          var downloadedPathwayDataUpdatedDate = new Date(downloadedPathwayData.updated);

          if (downloadedPathwayDataUpdatedDate >= bundledPathwayDataUpdatedDate) {
            latestPathwayData = downloadedPathwayData;
          } else {
            latestPathwayData = bundledPathwayData;
          }
        } else {
          latestPathwayData = bundledPathwayData;
        }
      } else if (downloadedPathwayData != null) {
        latestPathwayData = downloadedPathwayData;
      } else if (bundledPathwayData != null) {
        latestPathwayData = bundledPathwayData;
      }

      if (latestPathwayData != null) {
        for (let pathwayIndex = 0; pathwayIndex < latestPathwayData.Pathways.length; pathwayIndex++) {
          let pathway = latestPathwayData.Pathways[pathwayIndex];

          let pathwayName = "";
          if (pathway.fields["Pathway name"]) {
            pathwayName = pathway.fields["Pathway name"];
          }

          let pathwaySubtitle = "";
          let pathwayAreas: string[] = [];
          if (pathway.fields.Area) {
            pathwayAreas = pathway.fields.Area;
            pathway.areas = pathwayAreas;
          }

          let pathwayCategory = "";
          let pathwayCategoryIDs = pathway.fields["Pathway Categories"];
          let pathwayCategories = [];
          let pathwayTypes = [];
          if (pathwayCategoryIDs) {
            for (let pathwayCategoryID of pathwayCategoryIDs) {
              let category = latestPathwayData.categories.find((category) => category.id === pathwayCategoryID);
              if (category != null) {
                if (category.fields.Category != null) {
                  pathwayCategories.push(category.fields.Category);
                }
                if (category.fields["Pathway Type"] != null) {
                  pathwayTypes.push(category.fields["Pathway Type"]);
                }
              }
            }
          }

          if (pathwayCategories.length > 0) {
            pathway.categories = pathwayCategories;
          }
        }
      }

      return latestPathwayData;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  getPathWayImageName(pathway: IPathway, pathwayImageFilename: string) {
    let pathwayImageName = pathway.fields["Pathway ID"] + pathwayImageFilename;
    pathwayImageName = pathwayImageName.replace(/ /g, ""); // todo changed regex - check still works
    pathwayImageName = pathwayImageName.replace(/\//g, "");
    return pathwayImageName;
  }

  getPathwayWhatGoesWhereImageName(whatGoesWhere: IPathwayWhatGoesWhere, pathwayImageFilename: string) {
    let pathwayImageName = whatGoesWhere.id + pathwayImageFilename;
    pathwayImageName = pathwayImageName.replace(/ /g, "");
    pathwayImageName = pathwayImageName.replace(/\//g, "");
    return pathwayImageName;
  }

  async getNotifications(): Promise<INotification[]> {
    try {
      let updates = await Preferences.get({ key: "notifications" });

      if (updates.value) {
        let notifications = JSON.parse(updates.value);
        notifications = _.orderBy(notifications, ["date"], ["desc"]);
        return notifications;
      }

      return [];
    } catch (error) {
      console.log(error);

      return [];
    }
  }

  async saveNotifications(notifications: INotification[]) {
    try {
      await Preferences.set({ key: "notifications", value: JSON.stringify(notifications) });
    } catch (error) {
      console.log(error);
    }
  }

  async getTrendingSearches(trust: string, testMode: boolean): Promise<string[]> {
    let trendingSearches: string[] = [];

    if (trust === "") {
      return trendingSearches;
    }

    try {
      // try getting downloaded trendingSearches
      let path = `${trust}-trendingSearches.json`;
      if (testMode) {
        path = `${trust}-trendingSearches.TEST.json`;
      }

      let contents = await Filesystem.readFile({
        path: path,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      trendingSearches = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    return trendingSearches;
  }

  async getAppFeatures() {
    let appFeatures: IAppFeature[] = [];

    try {
      let contents = await Filesystem.readFile({
        path: `appFeatures.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      appFeatures = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    if (appFeatures == null || appFeatures.length <= 0) {
      var bundledAppFeatures: IAppFeature[] = require(`../assets/json/appFeatures.json`);
      if (bundledAppFeatures != null) {
        appFeatures = bundledAppFeatures;
      }
    }

    return appFeatures;
  }

  async getAppNotices() {
    let appNotices: IAppNotice[] = [];

    try {
      let contents = await Filesystem.readFile({
        path: `appNotices.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      appNotices = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    return appNotices;
  }

  async getFurtherResources() {
    let furtherResources: IFurtherResource[] = [];

    try {
      let contents = await Filesystem.readFile({
        path: `furtherResources.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      furtherResources = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    if (furtherResources == null || furtherResources.length <= 0) {
      var bundledFurtherResources: IFurtherResource[] = require(`../assets/json/furtherResources.json`);
      if (bundledFurtherResources != null) {
        furtherResources = bundledFurtherResources;
      }
    }

    return furtherResources;
  }

  async getImagesForFurtherResources(furtherResources: IFurtherResource[]) {
    let images = [];

    for (let furtherResourceIndex = 0; furtherResourceIndex < furtherResources.length; furtherResourceIndex++) {
      let furtherResource = furtherResources[furtherResourceIndex];
      let contents;

      if (furtherResource.iconUrl != null && furtherResource.iconUrl !== "" && furtherResource.iconFileName != null) {
        let fileName = furtherResource.iconFileName;

        try {
          contents = await Filesystem.readFile({
            path: `furtherResourceImages/${fileName}`,
            directory: Directory.Data,
          });

          contents.data = contents.data as string;
          contents.data = contents.data.replace(/"/g, "");

          let imageType = "jpeg";
          if (fileName.includes(".png") || fileName.includes(".PNG")) {
            imageType = "png";
          }
          contents.data = "data:image/" + imageType + ";base64, " + contents.data;

          let image = {
            name: fileName,
            data: contents.data,
          };

          images.push(image);
        } catch (error) {
          console.log(error);
          contents = {
            data: "",
          };
        }
      }
    }

    return images;
  }

  async getTrustPins(trust: string, testMode: boolean, app: IScreenProps["app"]): Promise<ITrustPin[]> {
    if (trust !== "") {
      // get local trust pins
      try {
        let path = `${trust}-trustPins.json`;
        if (testMode) {
          path = `${trust}-trustPins.TEST.json`;
        }

        let contents = await Filesystem.readFile({
          path: path,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        let trustPins: ITrustPin[] = JSON.parse(contents.data as string);

        if (trustPins.length > 0) {
          let filteredTrustPins = trustPins.filter((trustPin) => trustPin.enabled);
          filteredTrustPins = trustPins.filter(
            (trustPin) => trustPin.products == null || trustPin.products.includes(app)
          );
          // filteredTrustPins = trustPins

          return filteredTrustPins;
        }
      } catch (error) {
        console.log(error);
      }
    }

    return [];
  }

  async getImagesForTrustPins(trustPins: ITrustPin[]) {
    let images = [];

    for (let trustPinIndex = 0; trustPinIndex < trustPins.length; trustPinIndex++) {
      let trustPin = trustPins[trustPinIndex];
      let contents;

      if (trustPin.iconUrl != null && trustPin.iconUrl !== "" && trustPin.iconFileName != null) {
        let fileName = trustPin.iconFileName;

        try {
          contents = await Filesystem.readFile({
            path: `furtherResourceImages/${fileName}`,
            directory: Directory.Data,
          });

          contents.data = contents.data as string;
          contents.data = contents.data.replace(/"/g, "");

          let imageType = "jpeg";
          if (fileName.includes(".png") || fileName.includes(".PNG")) {
            imageType = "png";
          }
          contents.data = "data:image/" + imageType + ";base64, " + contents.data;

          let image = {
            name: fileName,
            data: contents.data,
          };

          images.push(image);
        } catch (error) {
          console.log(error);
          contents = {
            data: "",
          };
        }
      }
      if (trustPin.iconDarkUrl != null && trustPin.iconDarkUrl !== "" && trustPin.iconDarkFileName != null) {
        let fileName = trustPin.iconDarkFileName;

        try {
          contents = await Filesystem.readFile({
            path: `furtherResourceImages/${fileName}`,
            directory: Directory.Data,
          });
          contents.data = contents.data as string;
          contents.data = contents.data.replace(/"/g, "");

          let imageType = "jpeg";
          if (fileName.includes(".png") || fileName.includes(".PNG")) {
            imageType = "png";
          }
          contents.data = "data:image/" + imageType + ";base64, " + contents.data;

          let image = {
            name: fileName,
            data: contents.data,
          };

          images.push(image);
        } catch (error) {
          console.log(error);
          contents = {
            data: "",
          };
        }
      }
    }

    return images;
  }

  async getRegions(trust: string, testMode: boolean): Promise<IRegion[]> {
    if (trust !== "") {
      // get regions
      try {
        let path = `${trust}-regions.json`;
        if (testMode) {
          path = `${trust}-regions.TEST.json`;
        }

        let contents = await Filesystem.readFile({
          path: path,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        let regions: IRegion[] = JSON.parse(contents.data as string);

        if (regions.length > 0) {
          return regions;
        }
      } catch (error) {
        console.log(error);
      }
    }

    return [];
  }

  async getStaffGroups(trust: string, testMode: boolean): Promise<IStaffGroup[]> {
    if (trust !== "") {
      // get local staff groups
      try {
        let path = `${trust}-staffGroups.json`;
        if (testMode) {
          path = `${trust}-staffGroups.TEST.json`;
        }

        let contents = await Filesystem.readFile({
          path: path,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        let staffGroups: IStaffGroup[] = JSON.parse(contents.data as string);

        if (staffGroups.length > 0) {
          return staffGroups;
        }
      } catch (error) {
        console.log(error);
      }
    }

    return [];
  }

  async getRestrictedSections(trust: string, testMode: boolean): Promise<IRestrictedSection[]> {
    if (trust !== "") {
      // get local staff groups
      try {
        let path = `${trust}-restrictedSections.json`;
        if (testMode) {
          path = `${trust}-restrictedSections.TEST.json`;
        }

        let contents = await Filesystem.readFile({
          path: path,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        let restrictedSections: IRestrictedSection[] = JSON.parse(contents.data as string);

        if (restrictedSections.length > 0) {
          return restrictedSections;
        }
      } catch (error) {
        console.log(error);
      }
    }

    return [];
  }

  async getHelpCentreItems() {
    let helpCentreItems: IHelpCentreItem[] = [];

    try {
      let contents = await Filesystem.readFile({
        path: `helpCentreItems.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      helpCentreItems = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    if (helpCentreItems == null || helpCentreItems.length <= 0) {
      var bundledHelpCentreItems: IHelpCentreItem[] = require(`../assets/json/helpCentreItems.json`);
      if (bundledHelpCentreItems != null) {
        helpCentreItems = bundledHelpCentreItems;
      }
    }

    return helpCentreItems;
  }

  async getImagesForHelpCentreItems(helpCentreItems: IHelpCentreItem[]) {
    let images = [];

    for (let helpCentreItemIndex = 0; helpCentreItemIndex < helpCentreItems.length; helpCentreItemIndex++) {
      let helpCentreItem = helpCentreItems[helpCentreItemIndex];
      let contents;

      if (helpCentreItem.imageUrl != null && helpCentreItem.imageUrl !== "" && helpCentreItem.imageFileName != null) {
        let fileName = helpCentreItem.imageFileName;

        try {
          contents = await Filesystem.readFile({
            path: `helpImages/${fileName}`,
            directory: Directory.Data,
          });

          contents.data = contents.data as string;
          contents.data = contents.data.replace(/"/g, "");

          let imageType = "jpeg";
          if (fileName.includes(".png") || fileName.includes(".PNG")) {
            imageType = "png";
          }
          contents.data = "data:image/" + imageType + ";base64, " + contents.data;

          let image = {
            name: fileName,
            data: contents.data,
          };

          images.push(image);
        } catch (error) {
          console.log(error);
          contents = {
            data: "",
          };
        }
      }
    }

    return images;
  }

  async getTrustInfo() {
    let trustInfo: ITrustInfo[] = [];

    try {
      let contents = await Filesystem.readFile({
        path: `trustInfoTextItems.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      trustInfo = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    if (trustInfo == null || trustInfo.length <= 0) {
      var bundledTrustInfo: ITrustInfo[] = require(`../assets/json/trustInfoTextItems.json`);
      if (bundledTrustInfo != null) {
        trustInfo = bundledTrustInfo;
      }
    }

    return trustInfo;
  }

  async getSectionColors() {
    let sectionColors: ISectionColor[] = [];

    try {
      let contents = await Filesystem.readFile({
        path: `sectionColors.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      sectionColors = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    return sectionColors;
  }

  async getCFRbyTrustItems(testMode: boolean) {
    let cfrByTrustItems: ICFRbyTrustItem[] = [];

    let path = `CFRbyTrust.json`;
    if (testMode) {
      path = `CFRbyTrust.TEST.json`;
    }

    try {
      let contents = await Filesystem.readFile({
        path: path,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      cfrByTrustItems = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);
    }

    if (cfrByTrustItems == null || cfrByTrustItems.length <= 0) {
      var bundledCFRbyTrustItems: ICFRbyTrustItem[] = require(`../assets/json/CFRbyTrust.json`);
      if (bundledCFRbyTrustItems != null) {
        cfrByTrustItems = bundledCFRbyTrustItems;
      }
    }

    return cfrByTrustItems;
  }

  async validateImages() {}

  async getAbbreviations(trust: string): Promise<IAbbreviation[]> {
    let abbreviations: IAbbreviation[] = [];

    if (trust != null && trust !== "") {
      try {
        let bundledAbbreviations = (await import(`../../Data/${trust}/JSON/Abrrevs.json`)).default;
        if (bundledAbbreviations != null) {
          abbreviations = bundledAbbreviations;
        }
      } catch (error) {
        console.log(error);
      }
    }

    return abbreviations;
  }

  getSectionTitle(section: string) {
    if (section.includes("Section1")) {
      return "General Guidance";
    } else if (section.includes("Section2")) {
      return "Resuscitation";
    } else if (section.includes("Section3")) {
      return "Medical";
    } else if (section.includes("Section4")) {
      return "Trauma";
    } else if (section.includes("Section5")) {
      return "Maternity Care";
    } else if (section.includes("Section6")) {
      return "JRCALC Meds";
    } else if (section.includes("Section7")) {
      return "Special Situations";
    } else {
      return section;
    }
  }

  isDrug(guidelineOrSummary: IGuideline | IGuidelineSummary) {
    if (guidelineOrSummary.Section && guidelineOrSummary.Section.includes("Section6")) {
      return true;
    }

    let guidelineType = "";

    // typescript checking property exists on interface as could be IGuideline or IGuidelineSummary
    if ("GuidelineType" in guidelineOrSummary && guidelineOrSummary.GuidelineType != null) {
      guidelineType = guidelineOrSummary.GuidelineType;
    } else if ("publ_guideline_type" in guidelineOrSummary) {
      guidelineType = guidelineOrSummary.publ_guideline_type;
    }

    if (
      guidelineOrSummary.Section === "" &&
      (guidelineType === "Patient Group Direction" ||
        guidelineType === "Prescription Medicine Authorisation" ||
        guidelineType === "Medicines Protocol")
    ) {
      return true;
    }

    var SODrug = ["S0030", "S0040", "S0050", "S0060", "S0070", "S0080", "S0090"];
    if (SODrug.indexOf(guidelineOrSummary.GLID) != -1) {
      return true;
    }
    // if (guidelineOrSummary.GLID.indexOf('D') == 0) {
    //     return true;
    // }
    return false;
  }

  /** Use when checking for either a PGD, PMA or SMG - use isPGD, isPMA or isSMG for specific handling for them */
  isAuthorisableGuideline(guideline: IGuidelineSummary) {
    if (
      guideline.GuidelineType &&
      (guideline.GuidelineType.includes("Patient Group Direction") ||
        guideline.GuidelineType.includes("Prescription Medicine Authorisation") ||
        guideline.GuidelineType.includes("Supplementary Medication Guideline") ||
        guideline.GuidelineType.includes("Medication Supply Guideline"))
    ) {
      return true;
    }
    return false;
  }

  /** Use when specifically checking for PGD - otherwise use isAuthorisableGuideline */
  isPGD(guideline: IGuidelineSummary) {
    if (guideline.GuidelineType && guideline.GuidelineType.includes("Patient Group Direction")) {
      return true;
    }
    return false;
  }

  /** Use when specifically checking for PMA - otherwise use isAuthorisableGuideline */
  isPMA(guideline: IGuidelineSummary) {
    if (guideline.GuidelineType && guideline.GuidelineType.includes("Prescription Medicine Authorisation")) {
      return true;
    }
    return false;
  }

  /** Use when specifically checking for SMG - otherwise use isAuthorisableGuideline */
  isSMG(guideline: IGuidelineSummary) {
    if (
      guideline.GuidelineType &&
      (guideline.GuidelineType.includes("Supplementary Medication Guideline") ||
        guideline.GuidelineType.includes("Medication Supply Guideline"))
    ) {
      return true;
    }
    return false;
  }

  isSharedProcedure(guideline: IGuidelineSummary) {
    if (guideline.GuidelineType && guideline.GuidelineType.includes("Shared Procedure")) {
      return true;
    }
    return false;
  }

  isPathway(object: IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere): object is IPathway {
    return Object.prototype.hasOwnProperty.call(object.fields, "Pathway name");
  }

  isBypassNumber(object: IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere): object is IPathwayBypassNumber {
    return Object.prototype.hasOwnProperty.call(object.fields, "Pathway category");
  }

  isWhatGoesWhere(object: IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere): object is IPathwayWhatGoesWhere {
    return Object.prototype.hasOwnProperty.call(object.fields, "Name");
  }

  getPathwayName(pathway: IPathway | IPathwayBypassNumber | IPathwayWhatGoesWhere) {
    let pathwayName = "";
    if (this.isPathway(pathway) && pathway.fields["Pathway name"]) {
      pathwayName = pathway.fields["Pathway name"];
    } else if (this.isBypassNumber(pathway) && pathway.fields["Pathway category"]) {
      pathwayName = pathway.fields["Pathway category"];
    } else if (this.isWhatGoesWhere(pathway) && pathway.fields.Pathway) {
      pathwayName = pathway.fields.Pathway;
    }
    return pathwayName;
  }

  getPGDStatus(
    guidelineSummary: IGuidelineSummary,
    pgdAck: IPGDAck | null | undefined,
    pgdTrustInfo: IPGDTrustInfo | null
  ):
    | "NoAuth"
    | "InformationOnly"
    | "UnauthorisedTakeQuiz"
    | "UnauthorisedQuizTaken"
    | "UnauthorisedAcknowledge"
    | "Authorised" {
    let pgdStatus:
      | "NoAuth"
      | "InformationOnly"
      | "UnauthorisedTakeQuiz"
      | "UnauthorisedQuizTaken"
      | "UnauthorisedAcknowledge"
      | "Authorised" = "NoAuth";

    if (guidelineSummary.GLID === "SWT0560") {
      let test = "";
    }

    if (guidelineSummary.informationOnly) {
      pgdStatus = "InformationOnly";
    } else if (guidelineSummary.PGDType != null) {
      // PGDType overrides pgdTrustInfo settings
      if (guidelineSummary.PGDType === "NoAuth") {
        pgdStatus = "NoAuth";
      } else if (guidelineSummary.PGDType === "Auth") {
        if (pgdAck != null) {
          if (pgdAck.ACKTime != null && pgdAck.ACKTime !== "") {
            pgdStatus = "Authorised";
          } else {
            pgdStatus = "UnauthorisedAcknowledge";
          }
        } else {
          pgdStatus = "UnauthorisedAcknowledge";
        }
      } else if (guidelineSummary.PGDType === "AuthWithQuiz") {
        if (pgdAck != null && pgdTrustInfo != null) {
          if (pgdAck.ACKTime != null && pgdAck.ACKTime !== "") {
            pgdStatus = "Authorised";
          } else if (pgdAck.QuizAttemptTime && pgdAck.QuizAttemptTime !== "") {
            if (pgdTrustInfo.authorisationType == "Manual") {
              pgdStatus = "UnauthorisedQuizTaken";
            } else {
              pgdStatus = "UnauthorisedTakeQuiz";
            }
          } else {
            pgdStatus = "UnauthorisedTakeQuiz";
          }
        } else {
          pgdStatus = "UnauthorisedTakeQuiz";
        }
      }
    } else {
      if (pgdTrustInfo) {
        if (pgdTrustInfo.authorisationType === "No Authorisation or Dashboard") {
          pgdStatus = "NoAuth";
        } else if (pgdTrustInfo.hasQuizzes === "Yes") {
          if (pgdAck != null) {
            if (pgdAck.ACKTime != null && pgdAck.ACKTime !== "") {
              pgdStatus = "Authorised";
            } else if (pgdAck.QuizAttemptTime && pgdAck.QuizAttemptTime !== "") {
              if (pgdTrustInfo.authorisationType == "Manual") {
                pgdStatus = "UnauthorisedQuizTaken";
              } else {
                pgdStatus = "UnauthorisedTakeQuiz";
              }
            } else {
              pgdStatus = "UnauthorisedTakeQuiz";
            }
          } else {
            pgdStatus = "UnauthorisedTakeQuiz";
          }
        } else {
          if (pgdAck != null && pgdAck.ACKTime != null && pgdAck.ACKTime !== "") {
            pgdStatus = "Authorised";
          } else {
            pgdStatus = "UnauthorisedAcknowledge";
          }
        }
      }
    }

    return pgdStatus;
  }

  getAgesObjects(): IPatientAge[] {
    return [
      { name: "All", age: "All", months: -1, PFA: "none", range: "" },
      { name: "At Birth", age: "Infant", months: 0, PFA: "P0005", range: "0-0" },
      { name: "Post birth to 1 month", age: "Infant", months: 0, PFA: "P0010", range: "0-0" },
      { name: "1 month", age: "Infant", months: 1, PFA: "P0020", range: "1-2" },
      { name: "3 months", age: "Infant", months: 3, PFA: "P0030", range: "3-5" },
      { name: "6 months", age: "Infant", months: 6, PFA: "P0040", range: "6-8" },
      { name: "9 months", age: "Infant", months: 9, PFA: "P0050", range: "9-11" },
      { name: "12 months", age: "Infant", months: 12, PFA: "P0060", range: "12-17" },
      { name: "18 months", age: "Child", months: 18, PFA: "P0070", range: "18-23" },
      { name: "2 years", age: "Child", months: 24, PFA: "P0080", range: "24-35" },
      { name: "3 years", age: "Child", months: 36, PFA: "P0090", range: "36-47" },
      { name: "4 years", age: "Child", months: 48, PFA: "P0100", range: "48-59" },
      { name: "5 years", age: "Child", months: 60, PFA: "P0110", range: "60-71" },
      { name: "6 years", age: "Child", months: 72, PFA: "P0120", range: "72-83" },
      { name: "7 years", age: "Child", months: 84, PFA: "P0130", range: "84-95" },
      { name: "8 years", age: "Child", months: 96, PFA: "P0140", range: "96-107" },
      { name: "9 years", age: "Child", months: 108, PFA: "P0150", range: "108-119" },
      { name: "10 years", age: "Child", months: 120, PFA: "P0160", range: "120-131" },
      { name: "11 years", age: "Child", months: 132, PFA: "P0170", range: "132-143" },
      { name: "12 to 16 years", age: "Child", months: 144, PFA: "none", range: "144-191" },
      { name: "12 to Adult", age: "Adult", months: 144, PFA: "none", range: "144-9999" },
      { name: "16 to 18", age: "Adult", months: 192, PFA: "none", range: "192-215" },
      { name: "18+", age: "Adult", months: 216, PFA: "none", range: "216-9999" },
    ];
  }

  isPageForAge(GLID: string) {
    var result = GLID.indexOf("P0") == 0;
    return result;
  }

  /** pass selectedSection parameter to decide if annotation is in section */
  isAnnotationForDrugTable(annotation: IAnnotation, guideline: IGuideline, selectedSection?: any) {
    if (guideline.sections != null) {
      for (let section of guideline.sections) {
        // if selectedSection parameter passed then check if it matches
        if (selectedSection != null && section !== selectedSection) {
          continue;
        }

        if (section.TableAndBoxes) {
          for (let tableAndBox of section.TableAndBoxes) {
            if (tableAndBox.rowColData) {
              for (let rowColData of tableAndBox.rowColData) {
                if (rowColData.columns) {
                  for (let column of rowColData.columns) {
                    if (column.text === annotation.Reference) {
                      return true;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    return false;
  }

  getSectionClass(section: string) {
    if (
      section.includes("1 - General Guidance") ||
      section.includes("Section1") ||
      section.includes("General Guidance")
    ) {
      return "generalGuidance";
    } else if (
      section.includes("2 - Resuscitation") ||
      section.includes("Section2") ||
      section.includes("Resuscitation")
    ) {
      return "resuscitation";
    } else if (section.includes("3 - Medical") || section.includes("Section3") || section.includes("Medical")) {
      return "medical";
    } else if (section.includes("4 - Trauma") || section.includes("Section4") || section.includes("Trauma")) {
      return "trauma";
    } else if (
      section.includes("5 - Maternity Care") ||
      section.includes("Section5") ||
      section.includes("Maternity Care")
    ) {
      return "maternityCare";
    } else if (section.includes("6 - Drugs") || section.includes("Section6") || section.includes("Drugs")) {
      return "meds";
    } else if (
      section.includes("7 - Special Situations") ||
      section.includes("Section7") ||
      section.includes("Special Situations")
    ) {
      return "specialSituations";
    } else if (section.includes("Algorithms")) {
      return "algorithms";
    } else if (section.includes("Clinical Notice")) {
      return "clinicalNotice";
    } else if (section.includes("unacknowledged") || section.includes("Unacknowledged")) {
      return "unacknowledged";
    } else if (section.includes("acknowledged") || section.includes("Acknowledged")) {
      return "acknowledged";
    } else if (section.includes("Information Only") || section.includes("info-only")) {
      return "info-only";
    } else if (section.includes("Guidelines")) {
      return "sectionGuidelines";
    } else if (section.includes("Page for Age")) {
      return "pageForAge";
    } else if (section.includes("Meds")) {
      return "meds";
    } else if (section.includes("Essentials")) {
      return "essentials";
    } else if (section.includes("SignificantChangeJRCALC")) {
      return "significantChangeJRCALC";
    } else if (section.includes("Shared Procedure")) {
      return "sharedProcedure";
    } else if (section.includes("Title Matches")) {
      return "titleMatches";
    } else {
      let trustsWithColours = [
        "BRISTOW",
        "Class",
        "EEAST",
        "EMAS",
        "IOM",
        "JAS",
        "LAS",
        "NEAS",
        "NEOM",
        "NIAS",
        "NWAS",
        "ROLE1",
        "SAS",
        "SCAS",
        "SECAMB",
        "SJA",
        "SJARS",
        "SWAST",
        "TST",
        "WAS",
        "WMAS",
        "YAS",
      ];
      if (trustsWithColours.includes(section)) {
        return section;
      }

      return "defaultColor";
    }
  }

  getGuidelineClass(guideline: IGuidelineSummary): string {
    if (this.isPageForAge(guideline.GLID)) {
      return "pageForAge";
    } else {
      let section = "";

      if (this.isSharedProcedure(guideline)) {
        section = "Shared Procedure";
      } else if (guideline.Section.includes("Section1")) {
        section = "Section1";
      } else if (guideline.Section.includes("Section2")) {
        section = "Section2";
      } else if (guideline.Section.includes("Section3")) {
        section = "Section3";
      } else if (guideline.Section.includes("Section4")) {
        section = "Section4";
      } else if (guideline.Section.includes("Section5")) {
        section = "Section5";
      } else if (guideline.Section.includes("Section6")) {
        section = "Section6";
      } else if (guideline.Section.includes("Section7")) {
        section = "Section7";
      } else if (guideline.trust) {
        if (guideline.Section.includes("Section") === false) {
          section = guideline.trust;
        }
      }

      return this.getSectionClass(section);
    }
  }

  getTrusts(app: IScreenProps["app"]) {
    let trusts = [
      "BRISTOW",
      "Class",
      "EEAST",
      "EMAS",
      "IOM",
      "JAS",
      "LAS",
      "NEAS",
      "NEOM",
      "NIAS",
      "NWAS",
      "SAS",
      "SCAS",
      "SECAMB",
      "SJA",
      "SJARS",
      "SWAST",
      "TST",
      "WAS",
      "WMAS",
      "YAS",
    ];

    if (app === "CFR") {
      trusts.unshift("ACF");
      trusts.push("ROLE1");
    }

    return trusts;
  }

  // getTrustForGLID(glid: string) {
  //   if (glid.startsWith("ACF")) {
  //     return "ACF";
  //   } else if (glid.startsWith("BRIS")) {
  //     return "BRISTOW";
  //   } else if (glid.startsWith("EAST")) {
  //     return "EEAST";
  //   } else if (glid.startsWith("EM")) {
  //     return "EMAS";
  //   } else if (glid.startsWith("JAS")) {
  //     return "JAS";
  //   } else if (glid.startsWith("LAS")) {
  //     return "LAS";
  //   } else if (glid.startsWith("NEAS")) {
  //     return "NEAS";
  //   } else if (glid.startsWith("NIA")) {
  //     return "NIAS";
  //   } else if (glid.startsWith("NW")) {
  //     return "NWAS";
  //   } else if (glid.startsWith("SAS")) {
  //     return "SAS";
  //   } else if (glid.startsWith("SCA")) {
  //     return "SCAS";
  //   } else if (glid.startsWith("SEC")) {
  //     return "SECAMB";
  //   } else if (glid.startsWith("SJA")) {
  //     return "SJA";
  //   } else if (glid.startsWith("SJARS")) {
  //     return "SJARS";
  //   } else if (glid.startsWith("SWT")) {
  //     return "SWAST";
  //   } else if (glid.startsWith("WAS")) {
  //     return "WAS";
  //   } else if (glid.startsWith("WM")) {
  //     return "WMAS";
  //   } else if (glid.startsWith("YAS")) {
  //     return "YAS";
  //   } else {
  //     return null;
  //   }
  // }

  getTrustFromSubscription(subscription: any) {
    let id = subscription.SubscriptionID;

    if (id === 1186) {
      // CFR Group Subscription
      if (subscription.TrustName) {
        return subscription.TrustName;
      }
    }

    if (id === 5 || id === 6 || id === 7 || id === 8 || id === 9 || id === 10 || id === 12 || id === 14 || id === 77) {
      return "JRCALC";
    }

    if (id === 1393) {
      return "BRISTOW";
    }

    if (id === 1748) {
      return "Class";
    }

    if (id === 11) {
      return "EEAST";
    }

    if (id === 103) {
      return "EMAS";
    }

    if (id === 1591) {
      return "IOM";
    }

    if (id === 1394) {
      return "JAS";
    }

    if (id === 123) {
      return "LAS";
    }

    if (id === 115) {
      return "NEAS";
    }

    if (id === 1628) {
      return "NEOM";
    }

    if (id === 95) {
      return "NIAS";
    }

    if (id === 126) {
      return "NWAS";
    }

    if (id === 142) {
      return "SAS";
    }

    if (id === 110) {
      return "SCAS";
    }

    if (id === 98) {
      return "SECAMB";
    }

    if (id === 1158) {
      return "SJA";
    }

    if (id === 1478) {
      return "SJARS";
    }

    if (id === 102) {
      return "SWAST";
    }

    if (id === 1510) {
      return "TST";
    }

    if (id === 128) {
      return "WAS";
    }

    if (id === 113) {
      return "WMAS";
    }

    if (id === 104) {
      return "YAS";
    }

    // if (id === 1186) {
    // return "ACF";
    // }

    return "";
  }

  isSubscriptionForApp(subscription: any, app: IScreenProps["app"]) {
    let trust = this.getTrustFromSubscription(subscription);

    if (app === "iCPG" && trust === "JRCALC") {
      return true;
    } else if (app === "Plus") {
      if (trust !== "" && trust !== "JRCALC" && subscription.SubscriptionID !== 1186) {
        return true;
      }
    } else if (app === "CFR") {
      let validCFRSubscriptionIDs = [1182, 1183, 1184, 1185, 1186];
      if (validCFRSubscriptionIDs.includes(subscription.SubscriptionID)) {
        return true;
      }
    }

    return false;
  }

  isSubscriptionForParaPass(subscription: any): boolean {
    if (
      subscription.SubscriptionID === 61 ||
      subscription.SubscriptionID === 66 ||
      subscription.SubscriptionID === 90
    ) {
      return true;
    }

    if (subscription.Title === "EEAST Parapass Group Subscription") {
      return true;
    } else if (subscription.Title === "SECAmb ParaPass") {
      return true;
    } else if (subscription.Title === "WMAS ParaPass Group Subscription") {
      return true;
    } else if (subscription.Title === "NWAS Parapass group Subscription") {
      return true;
    } else if (
      subscription.Title === "LAS ParaPass Group Subscription Registrants" ||
      subscription.Title === "LAS ParaPass Group Subscription Registrant Managers"
    ) {
      return true;
    }

    return false;
  }

  isSubscriptionForParaFolio(subscription: any): boolean {
    if (subscription.Product === "ParaFolio") {
      return true;
    }

    return false;
  }

  hasParaPassSubscription(subscriptions: any[]) {
    let hasParaPassSubscription = false;
    let now = new Date();
    for (let subscriptionIndex = 0; subscriptionIndex < subscriptions.length; subscriptionIndex++) {
      let subscription: any = subscriptions[subscriptionIndex];
      if (this.isSubscriptionForParaPass(subscription)) {
        if (subscription.ExpiryDate != null && subscription.ExpiryDate !== "") {
          let subscriptionExpiryDateString = subscription.ExpiryDate.replace("/Date(", "").replace(")/", "");
          let expiryDateTimestamp = parseInt(subscriptionExpiryDateString);
          let expiryDate = new Date(expiryDateTimestamp);
          let subscriptionValid = expiryDate > now || expiryDateTimestamp === 0;
          if (subscriptionValid) {
            hasParaPassSubscription = true;
            break;
          }
        }
      }
    }

    return hasParaPassSubscription;
  }

  hasParaFolioSubscription(subscriptions: any[]) {
    let hasParaFolioSubscription = false;
    let now = new Date();
    for (let subscriptionIndex = 0; subscriptionIndex < subscriptions.length; subscriptionIndex++) {
      let subscription: any = subscriptions[subscriptionIndex];
      if (this.isSubscriptionForParaFolio(subscription)) {
        if (subscription.ExpiryDate != null && subscription.ExpiryDate !== "") {
          let subscriptionExpiryDateString = subscription.ExpiryDate.replace("/Date(", "").replace(")/", "");
          let expiryDateTimestamp = parseInt(subscriptionExpiryDateString);
          let expiryDate = new Date(expiryDateTimestamp);
          let subscriptionValid = expiryDate > now || expiryDateTimestamp === 0;
          if (subscriptionValid) {
            hasParaFolioSubscription = true;
            break;
          }
        }
      }
    }

    return hasParaFolioSubscription;
  }

  getTrustDisplayName(trust: string) {
    if (trust === "SECAMB") {
      return "SECAmb";
    } else if (trust === "WAS") {
      return "WAST";
    } else if (trust === "SWAST") {
      return "SWASFT";
    } else {
      return trust;
    }
  }

  getAppDisplayName(app: IScreenProps["app"]) {
    if (app === "iCPG") {
      return "iCPG";
    } else if (app === "Plus") {
      return "JRCALC Plus";
    } else if (app === "CFR") {
      return "Responder Plus";
    } else {
      return app;
    }
  }

  getTrustImage(trust: string, app: IScreenProps["app"]) {
    if (trust === "ACF") {
      return LogoTrustACF;
    } else if (trust === "BRISTOW") {
      return LogoTrustBRISTOW;
    } else if (trust === "Class") {
      return LogoTrustClass;
    } else if (trust === "EEAST") {
      return LogoTrustEEAST;
    } else if (trust === "EMAS") {
      return LogoTrustEMAS;
    } else if (trust === "IOM") {
      return LogoTrustIOM;
    } else if (trust === "JAS") {
      return LogoTrustJAS;
    } else if (trust === "LAS") {
      return LogoTrustLAS;
    } else if (trust === "NEAS") {
      return LogoTrustNEAS;
    } else if (trust === "NEOM") {
      return LogoTrustNEOM;
    } else if (trust === "NIAS") {
      return LogoTrustNIAS;
    } else if (trust === "NWAS") {
      return LogoTrustNWAS;
    } else if (trust === "ROLE1") {
      return LogoTrustROLE1;
    } else if (trust === "SAS") {
      return LogoTrustSAS;
    } else if (trust === "SECAMB") {
      return LogoTrustSECAMB;
    } else if (trust === "SJA") {
      return LogoTrustSJA;
    } else if (trust === "SJARS") {
      return LogoTrustSJARS;
    } else if (trust === "SWAST") {
      return LogoTrustSWAST;
    } else if (trust === "SCAS") {
      return LogoTrustSCAS;
    } else if (trust === "TST") {
      return LogoTrustTST;
    } else if (trust === "WAS") {
      return LogoTrustWAS;
    } else if (trust === "WMAS") {
      return LogoTrustWMAS;
    } else if (trust === "YAS") {
      return LogoTrustYAS;
    } else {
      return null;
    }
  }

  async getGuidelineAcknowledgements() {
    try {
      let acknowledged: any = await Preferences.get({ key: "guidelineAcknowledgements" });

      if (acknowledged.value === null) {
        return [];
      }

      return JSON.parse(acknowledged.value);
    } catch (error) {
      console.log(error);
    }
  }

  isCNAcknowledged(clinicalNotice: IClinicalNotice, clinicalNoticeAcks: IClinicalNoticeAck[]) {
    var isAcknowledged = false;

    for (let clinicalNoticeAckIndex = 0; clinicalNoticeAckIndex < clinicalNoticeAcks.length; clinicalNoticeAckIndex++) {
      let clinicalNoticeAck = clinicalNoticeAcks[clinicalNoticeAckIndex];

      if (clinicalNoticeAck.cnid === clinicalNotice.ID) {
        var acknowledgedDate = new Date(clinicalNoticeAck.timestamp);
        var noticeUpdatedDate = new Date(clinicalNotice.updated);

        if (acknowledgedDate >= noticeUpdatedDate) {
          isAcknowledged = true;
        }
      }
    }

    return isAcknowledged;
  }

  async getClinicalNotices(trust: string, testMode: boolean): Promise<IClinicalNotice[]> {
    let clinicalNotices: IClinicalNotice[] = [];

    if (trust === "") {
      return clinicalNotices;
    }

    try {
      // try getting downloaded clinical notices
      let path = `${trust}-clinicalNotices.json`;
      if (testMode) {
        path = `${trust}-clinicalNotices.TEST.json`;
      }

      let contents = await Filesystem.readFile({
        path: path,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      clinicalNotices = JSON.parse(contents.data as string);
    } catch (error) {
      console.log(error);

      // try getting bundled clinical notices
      if (testMode !== true) {
        try {
          let fileName = trust + "-clinicalNotices.json";
          let bundledJson = null;

          switch (trust) {
            case "BRISTOW":
              bundledJson = require(`../../Data/BRISTOW/JSON/${fileName}`);
              break;
            case "Class":
              bundledJson = require(`../../Data/Class/JSON/${fileName}`);
              break;
            case "EEAST":
              bundledJson = require(`../../Data/EEAST/JSON/${fileName}`);
              break;
            case "EMAS":
              bundledJson = require(`../../Data/EMAS/JSON/${fileName}`);
              break;
            case "IOM":
              bundledJson = require(`../../Data/IOM/JSON/${fileName}`);
              break;
            case "JAS":
              bundledJson = require(`../../Data/JAS/JSON/${fileName}`);
              break;
            case "LAS":
              bundledJson = require(`../../Data/LAS/JSON/${fileName}`);
              break;
            case "NEAS":
              bundledJson = require(`../../Data/NEAS/JSON/${fileName}`);
              break;
            case "NEOM":
              bundledJson = require(`../../Data/NEOM/JSON/${fileName}`);
              break;
            case "NIAS":
              bundledJson = require(`../../Data/NIAS/JSON/${fileName}`);
              break;
            case "NWAS":
              bundledJson = require(`../../Data/NWAS/JSON/${fileName}`);
              break;
            case "ROLE1":
              bundledJson = require(`../../Data/ROLE1/JSON/${fileName}`);
              break;
            case "SAS":
              bundledJson = require(`../../Data/SAS/JSON/${fileName}`);
              break;
            case "SCAS":
              bundledJson = require(`../../Data/SCAS/JSON/${fileName}`);
              break;
            case "SECAMB":
              bundledJson = require(`../../Data/SECAMB/JSON/${fileName}`);
              break;
            case "SJA":
              bundledJson = require(`../../Data/SJA/JSON/${fileName}`);
              break;
            case "SJARS":
              bundledJson = require(`../../Data/SJARS/JSON/${fileName}`);
              break;
            case "SWAST":
              bundledJson = require(`../../Data/SWAST/JSON/${fileName}`);
              break;
            case "TST":
              bundledJson = require(`../../Data/TST/JSON/${fileName}`);
              break;
            case "WAS":
              bundledJson = require(`../../Data/WAS/JSON/${fileName}`);
              break;
            case "WMAS":
              bundledJson = require(`../../Data/WMAS/JSON/${fileName}`);
              break;
            case "YAS":
              bundledJson = require(`../../Data/YAS/JSON/${fileName}`);
              break;

            default:
              break;
          }

          if (bundledJson.length > 0) {
            clinicalNotices = bundledJson;
          }
        } catch (error) {
          console.log(error);
        }
      }
    }

    clinicalNotices = _.orderBy(clinicalNotices, ["updated"], ["desc"]);
    return clinicalNotices;
  }

  async getClinicalNoticeAcks(): Promise<IClinicalNoticeAck[]> {
    try {
      let notices = await Preferences.get({ key: "clinicalNoticeAcks" });

      if (notices.value) {
        return JSON.parse(notices.value);
      }
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async saveClinicalNoticeAck(clinicalNoticeAck: IClinicalNoticeAck): Promise<IClinicalNoticeAck[]> {
    try {
      let acknowledged: any = await Preferences.get({ key: "clinicalNoticeAcks" });
      let array;

      if (acknowledged.value === null) {
        array = [clinicalNoticeAck];
      } else {
        array = JSON.parse(acknowledged.value);
        array.push(clinicalNoticeAck);
      }

      array.sort((a: any, b: any) => b.date - a.date);

      await Preferences.set({ key: "clinicalNoticeAcks", value: JSON.stringify(array) });

      return array;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async getSignificantChanges(): Promise<ISignificantChange[]> {
    try {
      let updates = await Preferences.get({ key: "significantChanges" });

      if (updates.value) {
        let significantChanges = JSON.parse(updates.value);
        significantChanges = _.orderBy(significantChanges, ["updated.dateObject"], ["desc"]);
        return significantChanges;
      }

      return [];
    } catch (error) {
      console.log(error);

      return [];
    }
  }

  async getSignificantChangeAcks(): Promise<ISignificantChangeAck[]> {
    try {
      let updates = await Preferences.get({ key: "significantChangeAcks" });

      if (updates.value) {
        return JSON.parse(updates.value);
      }

      return [];
    } catch (error) {
      console.log(error);

      return [];
    }
  }

  async saveSignificantChanges(significantChanges: ISignificantChange[]) {
    try {
      await Preferences.set({ key: "significantChanges", value: JSON.stringify(significantChanges) });
    } catch (error) {
      console.log(error);
    }
  }

  async saveSignificantChangeAck(significantChangeAck: ISignificantChangeAck): Promise<ISignificantChangeAck[]> {
    try {
      let acknowledged: any = await Preferences.get({ key: "significantChangeAcks" });
      let array;

      if (acknowledged.value === null) {
        array = [significantChangeAck];
      } else {
        array = JSON.parse(acknowledged.value);
        array.push(significantChangeAck);
      }

      array.sort((a: any, b: any) => b.date - a.date);

      await Preferences.set({ key: "significantChangeAcks", value: JSON.stringify(array) });

      return array;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async saveSignificantChangeAcks(significantChanges: ISignificantChangeAck[]) {
    try {
      await Preferences.set({ key: "significantChangeAcks", value: JSON.stringify(significantChanges) });
    } catch (error) {
      console.log(error);
    }
  }

  async getPGDAcks(): Promise<IPGDAck[]> {
    try {
      let pgdAcks = await Preferences.get({ key: "pgdAcks" });

      if (pgdAcks.value) {
        return JSON.parse(pgdAcks.value);
      }
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async savePGDAcks(pgdAcks: IPGDAck[]) {
    try {
      await Preferences.set({ key: "pgdAcks", value: JSON.stringify(pgdAcks) });
    } catch (error) {
      console.log(error);
    }
  }

  async savePGDAck(pgdAck: IPGDAck): Promise<IPGDAck[]> {
    let offlinePGDAcks: IPGDAck[] = [];

    try {
      let acknowledged: any = await Preferences.get({ key: "pgdAcks" });

      if (acknowledged.value === null) {
        offlinePGDAcks = [pgdAck];
      } else {
        offlinePGDAcks = JSON.parse(acknowledged.value);

        let matchingOfflineAckFound = false;

        for (let offlinePGDAckIndex = 0; offlinePGDAckIndex < offlinePGDAcks.length; offlinePGDAckIndex++) {
          let offlinePGDAck = offlinePGDAcks[offlinePGDAckIndex];

          if (pgdAck.GLID === offlinePGDAck.GLID) {
            // replace offline ack with new one
            offlinePGDAcks[offlinePGDAckIndex] = pgdAck;
            matchingOfflineAckFound = true;
            break;
          }
        }

        if (!matchingOfflineAckFound) {
          offlinePGDAcks.push(pgdAck);
        }
      }

      await Preferences.set({ key: "pgdAcks", value: JSON.stringify(offlinePGDAcks) });

      return offlinePGDAcks;
    } catch (error) {
      console.log(error);
    }

    return offlinePGDAcks;
  }

  async getGLAcks(): Promise<IGLAck[]> {
    try {
      let glAcks = await Preferences.get({ key: "glAcks" });

      if (glAcks.value) {
        return JSON.parse(glAcks.value);
      }
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async saveGLAck(glAck: IGLAck): Promise<IGLAck[]> {
    let offlineGLAcks: IGLAck[] = [];

    try {
      let acknowledged: any = await Preferences.get({ key: "glAcks" });

      if (acknowledged.value === null) {
        offlineGLAcks = [glAck];
      } else {
        offlineGLAcks = JSON.parse(acknowledged.value);

        let matchingOfflineAckFound = false;

        for (let offlineGLAckIndex = 0; offlineGLAckIndex < offlineGLAcks.length; offlineGLAckIndex++) {
          let offlineGLAck = offlineGLAcks[offlineGLAckIndex];

          if (glAck.GLID === offlineGLAck.GLID) {
            matchingOfflineAckFound = true;

            let onlineDate = new Date(glAck.timestamp);
            let offlineDate = new Date(offlineGLAck.timestamp);

            if (onlineDate > offlineDate) {
              // replace offline ack with new one
              offlineGLAcks[offlineGLAckIndex] = glAck;
            }
            break;
          }
        }

        if (!matchingOfflineAckFound) {
          offlineGLAcks.push(glAck);
        }
      }

      await Preferences.set({ key: "glAcks", value: JSON.stringify(offlineGLAcks) });

      return offlineGLAcks;
    } catch (error) {
      console.log(error);
    }

    return offlineGLAcks;
  }

  clearAcknowledgements() {
    try {
      Preferences.remove({ key: "significantChangeAcks" });
      Preferences.remove({ key: "clinicalNoticeAcks" });
      Preferences.remove({ key: "pgdAcks" });
      Preferences.remove({ key: "glAcks" });
    } catch (error) {
      console.log("Error clearing acknowledgements: ", error);
    }
  }

  async getPGDTrustInfo(trust: string) {
    if (trust === "") {
      return null;
    }

    let allPGDTrustInfo: IPGDTrustInfo[] = [];

    try {
      try {
        let contents = await Filesystem.readFile({
          path: `TrustInfo.json`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        allPGDTrustInfo = JSON.parse(contents.data as string);
      } catch (error) {
        console.log(error);
      }

      if (allPGDTrustInfo == null || allPGDTrustInfo.length <= 0) {
        var bundledPGDTrustInfo: IPGDTrustInfo[] = require(`../assets/json/pppTrustInfo.json`);
        if (bundledPGDTrustInfo != null) {
          allPGDTrustInfo = bundledPGDTrustInfo;
        }
      }

      let selectedPGDTrustInfo = allPGDTrustInfo.find((pgdTrustInfo) => pgdTrustInfo.trust === trust);

      if (selectedPGDTrustInfo != null) {
        return selectedPGDTrustInfo;
      }
    } catch (error) {
      console.log(error);
    }

    return null;
  }

  async getExternalCSS() {
    let externalCSS: string = "";

    try {
      try {
        let contents = await Filesystem.readFile({
          path: `ExternalStyle.css`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        let externalCSSString = contents.data as string;
        if (externalCSSString && externalCSSString.length > 0) {
          externalCSS = externalCSSString;
        }
      } catch (error) {
        console.log(error);
      }
    } catch (error) {
      console.log(error);
    }

    return externalCSS;
  }

  async guidelineHasParaPassQuiz(GLID: string): Promise<boolean> {
    let quizList = [];

    try {
      try {
        let contents = await Filesystem.readFile({
          path: `pppGuidelinesWithQuizzes.json`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        quizList = JSON.parse(contents.data as string);
      } catch (error) {
        console.log(error);
      }

      if (quizList != null && quizList.length > 0 && GLID !== "") {
        return quizList.includes(GLID);
      }
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  getEssentialSections() {
    let essentials = [];

    try {
      let essentialsFileName = "Essentials.json";
      essentials = require(`../../Data/${essentialsFileName}`);
    } catch (error) {
      // console.log('error getting bundled image' + error);
    }

    return essentials;
  }

  async saveBookmark(
    GLID: string,
    algorithmID: IBookmark["algorithmID"],
    clinicalNoticeID: IBookmark["clinicalNoticeID"],
    pathwayID: IBookmark["pathwayID"],
    type: IBookmark["type"]
  ): Promise<boolean> {
    try {
      let bookmarks: any = await Preferences.get({ key: "bookmarks" });

      let bookmark: IBookmark = {
        GLID: GLID,
        algorithmID: algorithmID,
        clinicalNoticeID: clinicalNoticeID,
        pathwayID: pathwayID,
        type: type,
        time: Date.now(),
      };

      // alert('saving bookmark: ' + GLID);

      if (bookmarks.value === null) {
        const array = [bookmark];
        await Preferences.set({ key: "bookmarks", value: JSON.stringify(array) });
        return true;
      } else {
        const existingBookmarks: any[] = JSON.parse(bookmarks.value);

        let index = -1;

        if (type === "guideline") {
          index = existingBookmarks.findIndex((_item: any) => _item.GLID === GLID);
        } else if (type === "algorithm") {
          index = existingBookmarks.findIndex((_item: any) => _item.algorithmID === algorithmID);
        } else if (type === "clinical notice") {
          index = existingBookmarks.findIndex((_item: any) => _item.clinicalNoticeID === clinicalNoticeID);
        } else if (type === "pathway") {
          index = existingBookmarks.findIndex((_item: any) => _item.pathwayID === pathwayID);
        } else if (type === "essentials") {
          index = existingBookmarks.findIndex((_item: any) => _item.GLID === GLID && _item.type === "essentials");
        }

        if (index === -1) {
          existingBookmarks.push(bookmark);
          await Preferences.set({ key: "bookmarks", value: JSON.stringify(existingBookmarks) });
          return true;
        } else {
          existingBookmarks.splice(index, 1);
          await Preferences.set({ key: "bookmarks", value: JSON.stringify(existingBookmarks) });
          return false;
        }
      }
    } catch (error) {
      console.log("Error saving bookmark: " + error);
    }

    return false;
  }

  async saveAllBookmarks(bookmarks: IBookmark[]) {
    try {
      await Preferences.set({ key: "bookmarks", value: JSON.stringify(bookmarks) });
    } catch (error) {
      console.log("Error saving all bookmarks: " + error);
    }
  }

  async getAllBookmarks(): Promise<IBookmark[]> {
    try {
      let bookmarks: any = await Preferences.get({ key: "bookmarks" });

      if (bookmarks.value === null) {
        await this.populateBookmarks();
        bookmarks = await Preferences.get({ key: "bookmarks" });
      }

      bookmarks = JSON.parse(bookmarks.value);

      bookmarks = bookmarks.sort((a: any, b: any) => b.time - a.time);

      return bookmarks;
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async populateBookmarks() {
    try {
      await this.saveBookmark("G0020", null, null, null, "guideline");
      await this.saveBookmark("G0120", null, null, null, "guideline");
      await this.saveBookmark("G0630", null, null, null, "guideline");
      await this.saveBookmark("D0090", null, null, null, "guideline");
      await this.saveBookmark("D0100", null, null, null, "guideline");
      await this.saveBookmark("D0215", null, null, null, "guideline");

      await this.saveBookmark("G0250", "G0250-59", null, null, "algorithm");
      await this.saveBookmark("G0355", "G0355-61", null, null, "algorithm");
      await this.saveBookmark("S0020", "S0020-57", null, null, "algorithm");
    } catch (error) {
      console.log(error);
    }
  }

  async getRecentSearches(): Promise<IRecentSearch[]> {
    try {
      let searches: any = await Preferences.get({ key: "recentSearch" });

      if (searches.value === null) {
        return [];
      }

      return JSON.parse(searches.value);
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async getRecentSearchPhrases(): Promise<string[]> {
    try {
      let searches: any = await Preferences.get({ key: "recentSearchPhrases" });

      if (searches.value === null) {
        return [];
      }

      return JSON.parse(searches.value);
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async getRecentlyViewed(): Promise<IRecentlyViewed[]> {
    try {
      let recents: any = await Preferences.get({ key: "recentlyViewed" });

      if (recents.value === null) {
        await this.populateRecentlyViewed();
        recents = await Preferences.get({ key: "recentlyViewed" });
      }

      let result = JSON.parse(recents.value);
      result = result.sort((a: any, b: any) => b.time - a.time);

      return result;
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async populateRecentlyViewed() {
    try {
      let recents: any = await Preferences.get({ key: "recentlyViewed" });

      if (recents.value === null) {
        await this.saveRecentlyViewed("G0020", null, null, null, "guideline");
        await this.saveRecentlyViewed("G0120", null, null, null, "guideline");
        await this.saveRecentlyViewed("G0630", null, null, null, "guideline");
        await this.saveRecentlyViewed("D0090", null, null, null, "guideline");
        await this.saveRecentlyViewed("D0100", null, null, null, "guideline");
        await this.saveRecentlyViewed("D0215", null, null, null, "guideline");

        await this.saveRecentlyViewed("G0250", "G0250-59", null, null, "algorithm");
        await this.saveRecentlyViewed("G0355", "G0355-61", null, null, "algorithm");
        await this.saveRecentlyViewed("S0020", "S0020-57", null, null, "algorithm");
      }
    } catch (error) {
      console.log(error);
    }
  }

  async saveRecentlyViewed(
    GLID: string,
    algorithmID: IRecentlyViewed["algorithmID"],
    clinicalNoticeID: IRecentlyViewed["clinicalNoticeID"],
    pathwayID: IRecentlyViewed["pathwayID"],
    type: IRecentlyViewed["type"]
  ) {
    if (GLID === "GAUTH") {
      return;
    }

    try {
      let recents: any = await Preferences.get({ key: "recentlyViewed" });

      let recentlyViewed: IRecentlyViewed = {
        GLID: GLID,
        algorithmID: algorithmID,
        clinicalNoticeID: clinicalNoticeID,
        pathwayID: pathwayID,
        type: type,
        time: Date.now(),
      };

      if (recents.value === null) {
        const array = [recentlyViewed];
        await Preferences.set({ key: "recentlyViewed", value: JSON.stringify(array) });
      } else {
        const array = JSON.parse(recents.value);

        let index = -1;
        if (type === "guideline") {
          index = array.findIndex((_item: any) => _item.GLID === GLID);
        } else if (type === "algorithm") {
          index = array.findIndex((_item: any) => _item.algorithmID === algorithmID);
        } else if (type === "clinical notice") {
          index = array.findIndex((_item: any) => _item.clinicalNoticeID === clinicalNoticeID);
        } else if (type === "pathway") {
          index = array.findIndex((_item: any) => _item.pathwayID === pathwayID);
        }

        if (index === -1) {
          if (array.length === 15) {
            array.splice(0, 1);
            array.push(recentlyViewed);
          } else {
            array.push(recentlyViewed);
          }

          await Preferences.set({ key: "recentlyViewed", value: JSON.stringify(array) });
        } else {
          array.splice(index, 1);
          array.push(recentlyViewed);
          await Preferences.set({ key: "recentlyViewed", value: JSON.stringify(array) });
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async saveAllRecentlyViewed(recentlyViewed: IRecentlyViewed[]) {
    await Preferences.set({ key: "recentlyViewed", value: JSON.stringify(recentlyViewed) });
  }

  async getAppDataLastUpdated() {
    try {
      let appDataLastUpdated: any = await Preferences.get({ key: "appDataLastUpdated" });

      if (appDataLastUpdated.value === null) {
        return 0;
      }

      return JSON.parse(appDataLastUpdated.value);
    } catch (error) {
      console.log(error);
    }

    return 0;
  }

  async saveAppDataLastUpdated(updated: number) {
    try {
      await Preferences.set({ key: "appDataLastUpdated", value: JSON.stringify(updated) });
    } catch (error) {
      console.log("Error saving appDataLastUpdated: " + error);
    }
  }

  async saveRecentSearch(searchResult: ISearchResult) {
    try {
      let searches: any = await Preferences.get({ key: "recentSearch" });

      let recentSearch: IRecentSearch = {
        type: searchResult.type,
      };

      if (searchResult.type === "guideline" && searchResult.guidelineSummary) {
        recentSearch.GLID = searchResult.guidelineSummary.GLID;
      } else if (searchResult.type === "algorithm" && searchResult.algorithm) {
        recentSearch.algorithmID = searchResult.algorithm.id;
      } else if (searchResult.type === "clinical notice" && searchResult.clinicalNotice) {
        recentSearch.clinicalNoticeID = searchResult.clinicalNotice.ID;
      } else if (searchResult.type === "pathway" && searchResult.pathway) {
        recentSearch.pathwayID = searchResult.pathway.id;
      } else if (searchResult.type === "essentials" && searchResult.essentialSection) {
        recentSearch.essentialSectionID = searchResult.essentialSection.id;
      } else if (searchResult.type === "further resource" && searchResult.furtherResource) {
        recentSearch.furtherResourceID = searchResult.furtherResource.resourceID;
      }

      if (searches.value === null) {
        const array = [recentSearch];
        await Preferences.set({ key: "recentSearch", value: JSON.stringify(array) });
      } else {
        const array: IRecentSearch[] = JSON.parse(searches.value);

        let matchingExistingResultIndex = -1;

        for (let existingRecentSearchIndex = 0; existingRecentSearchIndex < array.length; existingRecentSearchIndex++) {
          let existingRecentSearch = array[existingRecentSearchIndex];

          if (existingRecentSearch.type === "guideline" && existingRecentSearch.GLID === recentSearch.GLID) {
            matchingExistingResultIndex = existingRecentSearchIndex;
            break;
          } else if (
            existingRecentSearch.type === "algorithm" &&
            existingRecentSearch.algorithmID === recentSearch.algorithmID
          ) {
            matchingExistingResultIndex = existingRecentSearchIndex;
            break;
          } else if (
            existingRecentSearch.type === "clinical notice" &&
            existingRecentSearch.clinicalNoticeID === recentSearch.clinicalNoticeID
          ) {
            matchingExistingResultIndex = existingRecentSearchIndex;
            break;
          } else if (
            existingRecentSearch.type === "pathway" &&
            existingRecentSearch.pathwayID === recentSearch.pathwayID
          ) {
            matchingExistingResultIndex = existingRecentSearchIndex;
            break;
          } else if (
            existingRecentSearch.type === "essentials" &&
            existingRecentSearch.essentialSectionID === recentSearch.essentialSectionID
          ) {
            matchingExistingResultIndex = existingRecentSearchIndex;
            break;
          } else if (
            existingRecentSearch.type === "further resource" &&
            existingRecentSearch.furtherResourceID === recentSearch.furtherResourceID
          ) {
            matchingExistingResultIndex = existingRecentSearchIndex;
            break;
          }
        }

        if (matchingExistingResultIndex === -1) {
          if (array.length === 20) {
            array.pop();
            array.unshift(recentSearch);
          } else {
            array.unshift(recentSearch);
          }

          await Preferences.set({ key: "recentSearch", value: JSON.stringify(array) });
        } else {
          array.splice(matchingExistingResultIndex, 1);
          array.unshift(recentSearch);
          await Preferences.set({ key: "recentSearch", value: JSON.stringify(array) });
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async saveRecentSearchPhrase(phrase: string) {
    try {
      let trimmedPhrase = phrase.trim();

      let searches: any = await Preferences.get({ key: "recentSearchPhrases" });

      if (searches.value === null) {
        const array = [trimmedPhrase];
        await Preferences.set({ key: "recentSearchPhrases", value: JSON.stringify(array) });
      } else {
        const array: string[] = JSON.parse(searches.value);

        let matchingExistingResultIndex = array.indexOf(trimmedPhrase);

        if (matchingExistingResultIndex === -1) {
          if (array.length === 5) {
            array.pop();
            array.unshift(trimmedPhrase);
          } else {
            array.unshift(trimmedPhrase);
          }

          await Preferences.set({ key: "recentSearchPhrases", value: JSON.stringify(array) });
        } else {
          array.splice(matchingExistingResultIndex, 1);
          array.unshift(trimmedPhrase);
          await Preferences.set({ key: "recentSearchPhrases", value: JSON.stringify(array) });
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async clearRecentSearches() {
    try {
      await Preferences.remove({ key: "recentSearch" });
    } catch (error) {
      console.log(error);
    }
  }

  async clearRecentSearchPhrases() {
    await Preferences.remove({ key: "recentSearchPhrases" });
  }

  async getFontSize() {
    try {
      let size: any = await Preferences.get({ key: "fontSize" });

      if (size.value === null) {
        return 0;
      }

      return parseInt(size.value, 10);
    } catch (error) {
      console.log(error);
    }

    return 0;
  }

  async saveFontSize(fontSize: number) {
    try {
      await Preferences.set({ key: "fontSize", value: `${fontSize}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getLineHeight() {
    try {
      let lineHeight: any = await Preferences.get({ key: "lineHeight" });

      if (lineHeight.value === null) {
        return 1.5;
      }

      return parseFloat(lineHeight.value);
    } catch (error) {
      console.log(error);
    }

    return 1.5;
  }

  async saveLineHeight(lineHeight: number) {
    try {
      await Preferences.set({ key: "lineHeight", value: `${lineHeight}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getFontFamily(): Promise<string> {
    try {
      let fontFamily: any = await Preferences.get({ key: "fontFamily" });

      if (fontFamily.value === null) {
        return "Inter";
      }

      return fontFamily.value;
    } catch (error) {
      console.log(error);
    }

    return "Inter";
  }

  async saveFontFamily(fontFamily: string) {
    try {
      await Preferences.set({ key: "fontFamily", value: fontFamily });
    } catch (error) {
      console.log(error);
    }
  }

  async getShowTextToSpeech(): Promise<boolean> {
    try {
      let shouldShow: any = await Preferences.get({ key: "showTextToSpeech" });

      if (shouldShow.value === null) {
        return false;
      }

      return JSON.parse(shouldShow.value);
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveShowTextToSpeech(shouldShow: boolean) {
    try {
      await Preferences.set({ key: "showTextToSpeech", value: JSON.stringify(shouldShow) });
    } catch (error) {
      console.log(error);
    }
  }

  async getTextToSpeechSpeed(): Promise<number> {
    try {
      let textToSpeechSpeed: any = await Preferences.get({ key: "textToSpeechSpeed" });

      if (textToSpeechSpeed.value === null) {
        return 5;
      }

      return JSON.parse(textToSpeechSpeed.value);
    } catch (error) {
      console.log(error);
    }

    return 5;
  }

  async saveTextToSpeechSpeed(value: number) {
    try {
      await Preferences.set({ key: "textToSpeechSpeed", value: JSON.stringify(value) });
    } catch (error) {
      console.log(error);
    }
  }

  convertTextToSpeechSpeed(textToSpeechSpeed: number) {
    let speechSpeed = 1;

    if (isPlatform("ios")) {
      switch (textToSpeechSpeed) {
        case 1:
          speechSpeed = 0.5;
          break;
        case 2:
          speechSpeed = 0.6;
          break;
        case 3:
          speechSpeed = 0.8;
          break;
        case 4:
          speechSpeed = 0.9;
          break;
        case 5:
          speechSpeed = 1;
          break;
        case 6:
          speechSpeed = 2;
          break;
        case 7:
          speechSpeed = 3;
          break;
        case 8:
          speechSpeed = 4;
          break;
        case 9:
          speechSpeed = 5;
          break;
        default:
          break;
      }
    } else if (isPlatform("android")) {
      switch (textToSpeechSpeed) {
        case 1:
          speechSpeed = 0.5;
          break;
        case 2:
          speechSpeed = 0.6;
          break;
        case 3:
          speechSpeed = 0.8;
          break;
        case 4:
          speechSpeed = 0.9;
          break;
        case 5:
          speechSpeed = 1;
          break;
        case 6:
          speechSpeed = 1.5;
          break;
        case 7:
          speechSpeed = 2;
          break;
        case 8:
          speechSpeed = 3;
          break;
        case 9:
          speechSpeed = 4;
          break;
        default:
          break;
      }
    } else {
      switch (textToSpeechSpeed) {
        case 1:
          speechSpeed = 0.6;
          break;
        case 2:
          speechSpeed = 0.7;
          break;
        case 3:
          speechSpeed = 0.8;
          break;
        case 4:
          speechSpeed = 0.9;
          break;
        case 5:
          speechSpeed = 1;
          break;
        case 6:
          speechSpeed = 1.2;
          break;
        case 7:
          speechSpeed = 1.5;
          break;
        case 8:
          speechSpeed = 1.8;
          break;
        case 9:
          speechSpeed = 2;
          break;
        default:
          break;
      }
    }

    return speechSpeed;
  }

  async getFontWeight(): Promise<string> {
    try {
      let fontWeight: any = await Preferences.get({ key: "fontWeight" });

      if (fontWeight.value === null) {
        return "400";
      }

      return fontWeight.value;
    } catch (error) {
      console.log(error);
    }

    return "400";
  }

  async saveFontWeight(fontWeight: string) {
    try {
      await Preferences.set({ key: "fontWeight", value: fontWeight });
    } catch (error) {
      console.log(error);
    }
  }

  async getAccessabilityUnderlineLinks(): Promise<boolean> {
    try {
      let shouldUse: any = await Preferences.get({ key: "accessabilityUnderlineLinks" });

      if (shouldUse.value === null) {
        return false;
      }

      return JSON.parse(shouldUse.value);
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveAccessabilityUnderlineLinks(shouldUse: boolean) {
    try {
      await Preferences.set({ key: "accessabilityUnderlineLinks", value: JSON.stringify(shouldUse) });
    } catch (error) {
      console.log(error);
    }
  }

  async getHideParaPassLinks(): Promise<boolean> {
    try {
      let isHidden: any = await Preferences.get({ key: "hideParaPassLinks" });

      if (isHidden.value === null) {
        return false;
      }

      return JSON.parse(isHidden.value);
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveHideParaPassLinks(isHidden: boolean) {
    try {
      await Preferences.set({ key: "hideParaPassLinks", value: JSON.stringify(isHidden) });
    } catch (error) {
      console.log(error);
    }
  }

  async getABTestingVersion(): Promise<string> {
    try {
      let abTestingVersion: any = await Preferences.get({ key: "abTestingVersion" });

      if (abTestingVersion.value != null) {
        return abTestingVersion.value;
      }

      abTestingVersion = Math.random() < 0.5 ? "A" : "B";

      await Preferences.set({ key: "abTestingVersion", value: abTestingVersion });

      return abTestingVersion;
    } catch (error) {
      console.log(error);
    }

    return "A";
  }

  async getDarkMode() {
    try {
      let darkMode: any = await Preferences.get({ key: "darkMode" });

      if (darkMode.value === null) {
        return false;
      }

      return JSON.parse(darkMode.value);
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveDarkMode(darkMode: boolean) {
    try {
      await Preferences.set({ key: "darkMode", value: `${darkMode}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getAutomaticDarkMode() {
    try {
      let automaticDarkMode: any = await Preferences.get({ key: "automaticDarkMode" });

      if (automaticDarkMode.value === null) {
        return true;
      }

      return JSON.parse(automaticDarkMode.value);
    } catch (error) {
      console.log(error);
    }

    return true;
  }

  async saveAutomaticDarkMode(automaticDarkMode: boolean) {
    try {
      await Preferences.set({ key: "automaticDarkMode", value: `${automaticDarkMode}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getAppearance(): Promise<IScreenProps["appearance"]> {
    try {
      let appearance: any = await Preferences.get({ key: "appearance" });

      if (appearance.value === null) {
        // check if previously saved dark mode or automatic dark mode
        let darkMode = await this.getDarkMode();
        let automaticDarkMode = await this.getAutomaticDarkMode();

        if (automaticDarkMode) {
          this.saveAppearance("Auto");
          return "Auto";
        } else if (darkMode) {
          this.saveAppearance("Dark");
          return "Dark";
        } else {
          this.saveAppearance("Light");
          return "Light";
        }
      }

      return appearance.value;
    } catch (error) {
      console.log(error);
    }

    return "Auto";
  }

  async saveAppearance(appearance: IScreenProps["appearance"]) {
    try {
      await Preferences.set({ key: "appearance", value: `${appearance}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getShowColorFilter(): Promise<boolean> {
    try {
      let shouldShow: any = await Preferences.get({ key: "showColorFilter" });

      if (shouldShow.value === null) {
        return false;
      }

      return JSON.parse(shouldShow.value);
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveShowColorFilter(shouldShow: boolean) {
    try {
      await Preferences.set({ key: "showColorFilter", value: JSON.stringify(shouldShow) });
    } catch (error) {
      console.log(error);
    }
  }

  async getFilterColor(): Promise<IScreenProps["filterColor"]> {
    try {
      let filterColor: any = await Preferences.get({ key: "filterColor" });

      if (filterColor.value === null) {
        return { r: 0, g: 100, b: 255, a: 0.5 };
      }

      return JSON.parse(filterColor.value);
    } catch (error) {
      console.log(error);
    }

    return { r: 0, g: 100, b: 255, a: 0.5 };
  }

  async saveFilterColor(filterColor: IScreenProps["filterColor"]) {
    try {
      await Preferences.set({ key: "filterColor", value: JSON.stringify(filterColor) });
    } catch (error) {
      console.log(error);
    }
  }

  async getFreezeFirstDrugColumn(): Promise<boolean> {
    try {
      let shouldFreeze: any = await Preferences.get({ key: "freezeFirstDrugColumn" });

      if (shouldFreeze.value === null) {
        return false;
      }

      return JSON.parse(shouldFreeze.value);
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveFreezeFirstDrugColumn(shouldFreeze: boolean) {
    try {
      await Preferences.set({ key: "freezeFirstDrugColumn", value: JSON.stringify(shouldFreeze) });
    } catch (error) {
      console.log(error);
    }
  }

  async getKeepScreenAwake(): Promise<boolean> {
    try {
      let shouldKeepScreenAwake: any = await Preferences.get({ key: "keepScreenAwake" });

      if (shouldKeepScreenAwake.value === null) {
        return false;
      }

      return JSON.parse(shouldKeepScreenAwake.value);
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveKeepScreenAwake(shouldKeepScreenAwake: boolean) {
    try {
      await Preferences.set({ key: "keepScreenAwake", value: JSON.stringify(shouldKeepScreenAwake) });
    } catch (error) {
      console.log(error);
    }
  }

  async getNotificationsLastReadTime() {
    try {
      let notificationsLastReadTime: any = await Preferences.get({ key: "notificationsLastReadTime" });

      if (notificationsLastReadTime.value === null) {
        let lastReadTime = new Date().getTime();
        this.saveNotificationsLastReadTime(lastReadTime);
        return lastReadTime;
      }

      return parseInt(notificationsLastReadTime.value);
    } catch (error) {
      console.log(error);
    }

    return new Date().getTime();
  }

  async saveNotificationsLastReadTime(lastReadTime: number) {
    try {
      await Preferences.set({ key: "notificationsLastReadTime", value: `${lastReadTime}` });
    } catch (error) {
      console.log(error);
    }
  }

  defaultDashboardCardStates() {
    let dashboardCardStates: IDashboardCardState[] = [
      {
        name: "Updates & Notices",
        collapsed: false,
        reorderable: false,
        removable: false,
        removed: false,
        position: 0,
      },
      // {
      //   name: "Clinical Notices",
      //   collapsed: false,
      //   reorderable: true,
      //   removable: false,
      //   removed: false,
      //   position: 0,
      // },
      // {
      //   name: "Local Clinical Updates",
      //   collapsed: false,
      //   reorderable: true,
      //   removable: false,
      //   removed: false,
      //   position: 1,
      // },
      {
        name: "Trust Pins",
        collapsed: false,
        reorderable: true,
        removable: false,
        removed: false,
        position: 2,
      },
      {
        name: "Trust Guidelines",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 3,
      },
      // {
      //   name: "Patient Group Directions",
      //   collapsed: false,
      //   reorderable: true,
      //   removable: true,
      //   removed: false,
      //   position: 4,
      // },
      // {
      //   name: "Prescription Medicine Authorisations",
      //   collapsed: false,
      //   reorderable: true,
      //   removable: true,
      //   removed: false,
      //   position: 5,
      // },
      // {
      //   name: "Supplementary Medication Guidelines",
      //   collapsed: false,
      //   reorderable: true,
      //   removable: true,
      //   removed: false,
      //   position: 6,
      // },
      {
        name: "Shared Procedures",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 7,
      },
      {
        name: "COVID-19",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 8,
      },
      {
        name: "Time Critical",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 9,
      },
      {
        name: "Patient Age",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 10,
      },
      {
        name: "My Activity",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 11,
      },
      {
        name: "Staff Group",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 13,
      },
      {
        name: "Page for Age",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 14,
      },
      {
        name: "Further Resources",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 15,
      },
      // {
      //   name: "Guideline Updates",
      //   collapsed: false,
      //   reorderable: true,
      //   removable: true,
      //   removed: false,
      //   position: 16,
      // },
      {
        name: "Your subscriptions",
        collapsed: false,
        reorderable: true,
        removable: true,
        removed: false,
        position: 17,
      },
      {
        name: "Feedback",
        collapsed: false,
        reorderable: false,
        removable: false,
        removed: false,
        position: 18,
      },
      {
        name: "Disclaimer",
        collapsed: false,
        reorderable: false,
        removable: false,
        removed: false,
        position: 19,
      },
    ];

    return dashboardCardStates;
  }

  async getDashboardCardStates(
    trust: string,
    testMode: boolean,
    app: IScreenProps["app"]
  ): Promise<IDashboardCardState[]> {
    let dashboardCardStates = this.defaultDashboardCardStates();

    // add dashboard card states for trust pins which should be displayed as dashboard cards
    let trustPins = await this.getTrustPins(trust, testMode, app);

    for (let trustPinIndex = 0; trustPinIndex < trustPins.length; trustPinIndex++) {
      let trustPin = trustPins[trustPinIndex];
      if (trustPin.displayAsDashboardCard) {
        dashboardCardStates.push({
          name: trustPin.name,
          collapsed: false,
          reorderable: true,
          removable: false,
          removed: false,
          position: 3,
        });
      }
    }

    try {
      let savedDashboardCardStates: any = await Preferences.get({ key: "dashboardCardStates" });

      if (savedDashboardCardStates.value != null) {
        savedDashboardCardStates = JSON.parse(savedDashboardCardStates.value);

        for (let dashboardCardIndex = 0; dashboardCardIndex < dashboardCardStates.length; dashboardCardIndex++) {
          let dashboardCardState = dashboardCardStates[dashboardCardIndex];

          for (
            let savedDashboardCardIndex = 0;
            savedDashboardCardIndex < savedDashboardCardStates.length;
            savedDashboardCardIndex++
          ) {
            let savedDashboardCardState = savedDashboardCardStates[savedDashboardCardIndex];

            if (dashboardCardState.name === savedDashboardCardState.name) {
              dashboardCardState.collapsed = savedDashboardCardState.collapsed;

              if (savedDashboardCardState.position != null) {
                dashboardCardState.position = savedDashboardCardState.position;
              }

              if (savedDashboardCardState.removed != null) {
                dashboardCardState.removed = savedDashboardCardState.removed;
              }
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
    }

    dashboardCardStates = _.orderBy(dashboardCardStates, ["position"], ["asc"]);

    return dashboardCardStates;
  }

  async saveDashboardCardStates(dashboardCardStates: IDashboardCardState[]) {
    try {
      await Preferences.set({ key: "dashboardCardStates", value: JSON.stringify(dashboardCardStates) });
    } catch (error) {
      console.log(error);
    }
  }

  async getSubscribedPushNotificationTopics(): Promise<string[]> {
    try {
      let subscribedPushNotificationTopics: any = await Preferences.get({ key: "subscribedPushNotificationTopics" });

      if (subscribedPushNotificationTopics.value != null) {
        subscribedPushNotificationTopics = JSON.parse(subscribedPushNotificationTopics.value);
        return subscribedPushNotificationTopics;
      }
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async saveSubscribedPushNotificationTopics(topics: string[]) {
    try {
      await Preferences.set({ key: "subscribedPushNotificationTopics", value: JSON.stringify(topics) });
    } catch (error) {
      console.log(error);
    }
  }

  async saveDisclaimerDismissed() {
    try {
      await Preferences.set({ key: "disclaimerDismissed", value: `${true}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getLastOpenedGuidelineVersions(trust: string, app: IScreenProps["app"]) {
    // get all guideline summaries
    let allGuidelineSummaries = await this.getGuidelineSummaries(trust, app);

    // get last opened versions
    let lastOpenedGuidelineVersions: ILastOpenedGuidelineVersions = {};

    try {
      let savedLastOpenedGuidelineVersions: any = await Preferences.get({ key: "lastOpenedGuidelineVersions" });

      if (savedLastOpenedGuidelineVersions.value != null) {
        lastOpenedGuidelineVersions = JSON.parse(savedLastOpenedGuidelineVersions.value);
      }
    } catch (error) {
      console.log(error);
    }

    if (_.isEmpty(lastOpenedGuidelineVersions)) {
      // if last opened guideline versions is empty then app hasn't run before so set everything to current version (so all guidelines aren't listed as updated)
      for (
        let guidelineSummaryIndex = 0;
        guidelineSummaryIndex < allGuidelineSummaries.length;
        guidelineSummaryIndex++
      ) {
        let guidelineSummary = allGuidelineSummaries[guidelineSummaryIndex];

        lastOpenedGuidelineVersions[guidelineSummary.GLID] = {
          publ_major_version_no: guidelineSummary.publ_major_version_no,
          publ_minor_version_no: guidelineSummary.publ_minor_version_no,
          updated: false,
        };

        // save for next time
        await this.saveLastOpenedGuidelineVersions(lastOpenedGuidelineVersions);
      }
    } else {
      // decide if guideline updated based on difference in versions
      for (
        let guidelineSummaryIndex = 0;
        guidelineSummaryIndex < allGuidelineSummaries.length;
        guidelineSummaryIndex++
      ) {
        let guidelineSummary = allGuidelineSummaries[guidelineSummaryIndex];

        if (lastOpenedGuidelineVersions[guidelineSummary.GLID] != null) {
          let lastOpenedGuidelineVersion = lastOpenedGuidelineVersions[guidelineSummary.GLID];

          const currentGuidelineMajor = parseInt(guidelineSummary.publ_major_version_no);
          const currentGuidelineMinor = parseInt(guidelineSummary.publ_minor_version_no);
          const lastOpenedMajor = parseInt(lastOpenedGuidelineVersion.publ_major_version_no);
          const lastOpenedMinor = parseInt(lastOpenedGuidelineVersion.publ_minor_version_no);

          let updated = false;
          if (currentGuidelineMajor > lastOpenedMajor) {
            updated = true;
          } else if (currentGuidelineMajor === lastOpenedMajor && currentGuidelineMinor > lastOpenedMinor) {
            updated = true;
          }
          lastOpenedGuidelineVersions[guidelineSummary.GLID] = {
            publ_major_version_no: lastOpenedGuidelineVersion.publ_major_version_no,
            publ_minor_version_no: lastOpenedGuidelineVersion.publ_minor_version_no,
            updated: updated,
          };
        } else {
          // guideline must be new - so don't create an entry for it
        }
      }
    }

    return lastOpenedGuidelineVersions;
  }

  async saveLastOpenedGuidelineVersions(lastOpenedGuidelineVersions: ILastOpenedGuidelineVersions) {
    try {
      await Preferences.set({ key: "lastOpenedGuidelineVersions", value: JSON.stringify(lastOpenedGuidelineVersions) });
    } catch (error) {
      console.log(error);
    }
  }

  async getLocalUsageLog(): Promise<ILocalUsageLogItem[]> {
    try {
      let localUsageLog = await Preferences.get({ key: "localUsageLog" });

      if (localUsageLog.value === null) {
        return [];
      }

      return JSON.parse(localUsageLog.value);
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  async logLocalUsage(
    guideline: IGuidelineSummary | null,
    algorithm: IAlgorithm | null,
    clinicalNotice: IClinicalNotice | null,
    clinicalNoticeAck: IClinicalNoticeAck | null,
    significantChange: ISignificantChange | null,
    significantChangeAck: ISignificantChangeAck | null,
    userEmail: string
  ) {
    try {
      var now = new Date();
      var timestamp = now.toISOString();

      let id = "";
      let title = "";
      let type: ILocalUsageLogItem["type"] = "guideline";

      if (guideline != null) {
        id = guideline.GLID;
        title = guideline.Title;
        type = "guideline";
      } else if (algorithm != null) {
        id = "ALG-" + algorithm.id;
        title = algorithm.name;
        type = "algorithm";
      } else if (clinicalNotice != null && clinicalNoticeAck != null) {
        id = clinicalNotice.ID.toString();
        title = clinicalNotice.Title;
        type = "clinicalNoticeAck";
        timestamp = clinicalNoticeAck.timestamp;
      } else if (significantChange != null && significantChangeAck != null) {
        id = significantChange.id;
        title = significantChange.title;
        type = "significantChangeAck";
        timestamp = significantChangeAck.timestamp;
      }

      if (id !== "") {
        var localUsageLogItem: ILocalUsageLogItem = {
          userID: userEmail,
          id: id,
          title: title,
          timestamp: timestamp,
          type: type,
        };

        let localUsageLog = await this.getLocalUsageLog();
        localUsageLog.push(localUsageLogItem);

        try {
          await Preferences.set({ key: "localUsageLog", value: JSON.stringify(localUsageLog) });
        } catch (error) {
          console.log(error);
        }
      }
    } catch (error) {
      console.log("Error logging usage: " + error);
    }
  }

  async getPatientAge(): Promise<IPatientAge> {
    let patientAges = this.getAgesObjects();

    try {
      let patientAgeName: any = await Preferences.get({ key: "patientAge" });

      if (patientAgeName.value != null) {
        patientAgeName = patientAgeName.value;
        let patientAge = patientAges.find((patientAge) => patientAge.name === patientAgeName);

        if (patientAge != null) {
          return patientAge;
        }
      }
    } catch (error) {
      console.log(error);
    }

    return patientAges[0];
  }

  async savePatientAge(patientAge: IPatientAge) {
    try {
      await Preferences.set({ key: "patientAge", value: `${patientAge.name}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getRegion() {
    try {
      let region = await Preferences.get({ key: "region" });

      if (region.value) {
        return JSON.parse(region.value);
      }

      return null;
    } catch (error) {
      console.log(error);

      return null;
    }
  }

  async saveRegion(region: string | null) {
    try {
      await Preferences.set({ key: "region", value: JSON.stringify(region) });
    } catch (error) {
      console.log(error);
    }
  }

  async getTestMode(): Promise<boolean> {
    try {
      let test: any = await Preferences.get({ key: "testMode" });

      if (test.value != null) {
        return JSON.parse(test.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveTestMode(testMode: boolean) {
    try {
      await Preferences.set({ key: "testMode", value: `${testMode}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getDebugMode(): Promise<boolean> {
    try {
      let debug: any = await Preferences.get({ key: "debugMode" });

      if (debug.value != null) {
        return JSON.parse(debug.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveDebugMode(debugMode: boolean) {
    try {
      await Preferences.set({ key: "debugMode", value: `${debugMode}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getFinishedOnboarding(): Promise<boolean> {
    try {
      let finishedOnboarding: any = await Preferences.get({ key: "finishedOnboarding" });

      if (finishedOnboarding.value != null) {
        return JSON.parse(finishedOnboarding.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveFinishedOnboarding(finishedOnboarding: boolean) {
    try {
      await Preferences.set({ key: "finishedOnboarding", value: `${finishedOnboarding}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getCFRDisclaimerDismissed(): Promise<boolean> {
    try {
      let cfrDisclaimerDismissed: any = await Preferences.get({ key: "cfrDisclaimerDismissed" });

      if (cfrDisclaimerDismissed.value != null) {
        return JSON.parse(cfrDisclaimerDismissed.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveCFRDisclaimerDismissed(cfrDisclaimerDismissed: boolean) {
    try {
      await Preferences.set({ key: "cfrDisclaimerDismissed", value: `${cfrDisclaimerDismissed}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getNotificationBannerDismissed(): Promise<boolean> {
    try {
      let result: any = await Preferences.get({ key: "notificationBannerDismissed" });

      if (result.value != null) {
        return JSON.parse(result.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveNotificationBannerDismissed() {
    try {
      await Preferences.set({ key: "notificationBannerDismissed", value: `${true}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getWebBannerDismissed(): Promise<boolean> {
    try {
      let result: any = await Preferences.get({ key: "webBannerDismissed" });

      if (result.value != null) {
        return JSON.parse(result.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveWebBannerDismissed() {
    try {
      await Preferences.set({ key: "webBannerDismissed", value: `${true}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getCustomiseDashboardCardDismissed(): Promise<boolean> {
    try {
      let result: any = await Preferences.get({ key: "customiseDashboardCardDismissed" });

      if (result.value != null) {
        return JSON.parse(result.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveCustomiseDashboardCardDismissed() {
    try {
      await Preferences.set({ key: "customiseDashboardCardDismissed", value: `${true}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getAppNoticesCardDismissed(appNotice: IAppNotice): Promise<boolean> {
    try {
      let result: any = await Preferences.get({ key: "appNoticesCardDismissed-" + appNotice.ID });

      if (result.value != null) {
        return JSON.parse(result.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveAppNoticesCardDismissed(appNotice: IAppNotice) {
    try {
      await Preferences.set({ key: "appNoticesCardDismissed-" + appNotice.ID, value: `${true}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getBookmarkSuggestionDismissed(): Promise<boolean> {
    try {
      let result: any = await Preferences.get({ key: "bookmarkSuggestionDismissed" });

      if (result.value != null) {
        return JSON.parse(result.value);
      }

      return false;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async saveBookmarkSuggestionDismissed() {
    try {
      await Preferences.set({ key: "bookmarkSuggestionDismissed", value: `${true}` });
    } catch (error) {
      console.log(error);
    }
  }

  async getShouldShowOlark(): Promise<boolean> {
    try {
      if ((isPlatform("ios") || isPlatform("android")) && !isPlatform("mobileweb")) {
        return false;
      }

      let result: any = await Preferences.get({ key: "showOlark" });

      if (result.value != null && this.isDesktopVersion() === false) {
        return JSON.parse(result.value);
      }
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async getSSOTrusts(): Promise<string[]> {
    try {
      if (this.isDesktopVersion() === true) {
        return [];
      }

      if (window.location.origin === "https://jrcalcplusweb-sso-test.netlify.app") {
        return this.getTrusts("Plus");
      } else if (window.location.origin === "https://responderplus-sso-test.netlify.app") {
        return this.getTrusts("CFR");
      }

      let result: any = await Preferences.get({ key: "ssoTrusts" });

      if (result.value != null) {
        return JSON.parse(result.value);
      }
    } catch (error) {
      console.log(error);
    }

    return [];
  }

  addHighlight(sectionToSearch: string, searchString: string) {
    searchString = searchString.replace(/\\/g, "").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    let sectionHTML = sectionToSearch;
    const highlightStart = "<span class='searchHighlight'>";
    const highlightEnd = "</span>";
    let startIndices = this.getIndicesOf(sectionHTML, searchString);
    let start = 0;

    if (startIndices.length > 0) {
      for (let i = 0; i < startIndices.length; i++) {
        start = startIndices[i];
        const highlightDisplacement = (highlightStart.length + highlightEnd.length) * i;

        sectionHTML =
          sectionHTML.substr(0, start + highlightDisplacement) +
          highlightStart +
          sectionHTML.substr(start + highlightDisplacement, searchString.length) +
          highlightEnd +
          sectionHTML.substr(start + highlightDisplacement + searchString.length);
      }
    }

    return sectionHTML;
  }

  getIndicesOf(stringToSearch: string, searchString: string) {
    if (searchString === null || searchString.length === 0) {
      return [];
    }

    let startIndex = 0;
    let index;
    let tag;
    const indices: any[] = [];
    const htmlTags: any[] = [];

    searchString = searchString.toLowerCase();
    stringToSearch = stringToSearch.toLowerCase();

    let regex = /<(?:.|\n)*?>/gi;

    while ((tag = regex.exec(stringToSearch)) != null) {
      htmlTags.push({ tag: tag[0], index: tag.index });
    }

    while ((index = stringToSearch.indexOf(searchString, startIndex)) > -1) {
      let withinTag = false;

      for (let i = 0; i < htmlTags.length; i++) {
        const tag = htmlTags[i];
        let start = tag.index;
        let end = tag.index + tag.tag.length;

        if (index > start && index < end) {
          withinTag = true;
        }

        if (tag.tag.indexOf("<index-entry") !== -1) {
          if (index > tag.index && index < htmlTags[i + 1]) {
            withinTag = true;
          }
        }
      }

      if (!withinTag) {
        indices.push(index);
      }

      startIndex = index + searchString.length;
    }

    return indices;
  }

  withinTag(stringToSearch: string, searchString: string) {
    let within = false;

    return within;
  }

  isLocalLink(href: string) {
    let localLinks = [
      "jrcalcplusweb.co.uk",
      "icpgweb.co.uk",
      "cfrplus.co.uk",
      "responderplus.co.uk",
      "localhost",
      ":8100",
      "jrcalc-web.netlify.app",
      "icpg-test.netlify.app",
      "cfr.netlify.app",
    ];

    for (let localLink of localLinks) {
      if (href.includes(localLink)) {
        return true;
      }
    }
  }

  stripLocalLinks(href: string) {
    let newHref = href.replace("https://jrcalcplusweb.co.uk/", "");
    newHref = newHref.replace("https://icpgweb.co.uk/", "");
    newHref = newHref.replace("https://cfrplus.co.uk/", "");
    newHref = newHref.replace("https://responderplus.co.uk/", "");

    if (newHref.includes(":8100/")) {
      newHref = newHref.substr(newHref.indexOf(":8100/") + 6);
    } else if (newHref.includes("localhost/")) {
      newHref = newHref.substr(newHref.indexOf("localhost/") + 10);
    } else if (newHref.includes("jrcalc-web.netlify.app/")) {
      newHref = newHref.substr(newHref.indexOf("jrcalc-web.netlify.app/") + 23);
    } else if (newHref.includes("icpg-test.netlify.app/")) {
      newHref = newHref.substr(newHref.indexOf("icpg-test.netlify.app/") + 22);
    } else if (newHref.includes("cfr.netlify.app/")) {
      newHref = newHref.substr(newHref.indexOf("cfr.netlify.app/") + 16);
    }

    return newHref;
  }

  isDesktopVersion(): boolean {
    // using hard coded value as using isPlatform('electron') returns true for SWAST ePR
    return false;
  }

  async getAppVersion(app: IScreenProps["app"]): Promise<string> {
    let appVersion = "";

    if ((isPlatform("ios") || isPlatform("android")) && !isPlatform("mobileweb")) {
      let appInfo = await App.getInfo();

      if (isPlatform("ios")) {
        appVersion += "iOS ";
      } else if (isPlatform("android")) {
        appVersion += "Android ";
      }

      if (appInfo.version !== "") {
        appVersion += appInfo.version + "(" + appInfo.build + ")";
      }
    } else {
      if (this.isDesktopVersion()) {
        if (app === "iCPG") {
          appVersion = "Desktop 2.0.7";
        } else if (app === "Plus") {
          appVersion = "Desktop 2.7";
        } else if (app === "CFR") {
          appVersion = "Desktop 2.0";
        }
      } else {
        if (app === "iCPG") {
          appVersion = "Web 2.1";
        } else if (app === "Plus") {
          appVersion = "Web 2.8";
        } else if (app === "CFR") {
          appVersion = "Web 2.2";
        }
      }
    }

    return appVersion;
  }

  async waitForMilliseconds(time: number) {
    return new Promise((resolve) => setTimeout(resolve, time));
  }
}

export default new DataController();
